import _3Dconnexion, { type I3DConnexionClient } from '@3dconnexion/3dconnexionjs'
import * as THREE from 'three'
import { Controls } from 'three'
import { getLoggerManager } from '@/shared/logging'
import { type PhaseRenderContext, RenderPhase } from '@/shared/types/assets'
import { CameraMode, type CameraState, type ControlMode } from '@/shared/types/core'
import type { RenderContributor } from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import {
  CONTROL_GRAPHICS_REQUIREMENTS,
  controlOverlayGraphicsAdapter,
} from './ControlOverlayGraphicsAdapter'
import {
  type NativeControlSurfacePickingOptions,
  resolveNativeControlMinZoomStep,
  resolveNativeControlPickWindowSize,
} from './NativeControlSurfacePickingOptions'
import {
  accumulateWheelInputDelta,
  DEFAULT_NAVIGATION_SCALE_ABSOLUTE_MINIMUM,
  DEFAULT_WHEEL_ABSOLUTE_MIN_STEP,
  DEFAULT_WHEEL_MIN_STEP_RATIO,
  isCurrentViewportCamera,
  isPointBeyondNativeControlPickPlane,
  NATIVE_CONTROL_SURFACE_KINDS,
  type NativeControlServices,
  type NativeControlSurfaceQueryHit,
  resolveDoubleClickTargetRadius,
  resolveDoubleClickWheelDelta,
  resolveNativeControlPickMinDistanceAtScreen,
  resolveNavigationScale,
  resolvePickedPointWheelStep,
  resolveWheelForwardStep,
  resolveWheelInputDelta,
  shouldUseAsyncPicking,
  syncViewportCameraFromRig,
} from './NativeControlUtils'

const log = getLoggerManager().getLogger('control')

/** SpaceMouseController 构造参数 */
export interface SpaceMouseControllerOptions {
  /** 控制器共享服务集合 */
  services: NativeControlServices
  /** 表面支点的拾取近平面偏移 */
  surfacePicking?: NativeControlSurfacePickingOptions
  /** SpaceMouse 表面拾取窗口像素尺寸，默认 129。 */
  windowSize?: number
  /** SpaceMouse 缩放绝对最小步长，单位为世界坐标，默认 0.3。 */
  minZoomStep?: number
  /** 注册到 3Dconnexion 的应用名，默认 'SHARE3D Engine' */
  appName?: string
  /** 鼠标/设备旋转速度倍率 */
  rotateSpeed?: number
  /** 鼠标/设备平移速度倍率 */
  panSpeed?: number
  /** 滚轮缩放速度倍率 */
  zoomSpeed?: number
  /** 设备单位到米的换算系数，默认 10 */
  unitsToMeters?: number
  /** 鼠标与 3D 鼠标的导航预设。 */
  navigationProfile?: SpaceMouseNavigationProfile
  /** 3Dconnexion 设备在线状态提供器。 */
  presenceProvider?: SpaceMousePresenceProvider
  /** 设备在线状态轮询间隔，单位毫秒。 */
  presencePollIntervalMs?: number
}

/** 鼠标旋转模式：弧球或偏航俯仰 */
export type SpaceMouseMouseRotateMode = 'arcball' | 'yaw-pitch'
/** 鼠标导航预设：仅鼠标或混合 3D 鼠标。 */
export type SpaceMouseNavigationProfile = 'mouse-only' | 'hybrid'
/** 鼠标旋转 target 策略：bounds 中心或捕捉点。 */
export type SpaceMouseTargetMode = 'bounds-center' | 'captured-point'
/** 3Dconnexion 设备在线状态提供器。 */
export type SpaceMousePresenceProvider = () => boolean | Promise<boolean>
/** SpaceMouse 设备按钮映射到应用命令的回调集合。 */
export interface SpaceMouseAppCommandHandlers {
  /** 撤销命令回调。 */
  undo?: () => void
  /** 重做命令回调。 */
  redo?: () => void
}

type SpaceMouseCommandAction = {
  id: string
  label: string
  type: number
  description: string
}

type SpaceMouseCommandCategory = {
  id: string
  label: string
  type: number
  nodes: SpaceMouseCommandAction[]
}

type SpaceMouseCommandActionSet = {
  id: string
  label: string
  type: number
  nodes: SpaceMouseCommandCategory[]
}

type SpaceMouseCommandTree = {
  nodes: SpaceMouseCommandActionSet[]
}

const DEFAULT_APP_NAME = 'SHARE3D Engine'
const DEFAULT_EXTENT = 15
const DEFAULT_UNITS_TO_METERS = 10
const CAMERA_EPSILON = 1e-5
const PIVOT_RING_PIXEL_RADIUS = 44
const PIVOT_RING_BASE_RADIUS = 1
const PIVOT_RING_TUBE_RADIUS = 0.028
const PIVOT_CENTER_PIXEL_RADIUS = 7
const PIVOT_CENTER_BASE_RADIUS = 0.18
const DEFAULT_PICK_RADIUS = 0.08
const LEGACY_ROTATION_SPEED = 10
const LEGACY_WHEEL_FADE_FACTOR = 20
const LEGACY_WHEEL_STEP_RATIO = 0.2
const FREE_WHEEL_STEP_RATIO = 0.1
const MAX_ABSOLUTE_PITCH = Math.PI / 2 - 1e-3
const ROTATE_MODE_TRANSITION_DURATION_MS = 180
const DEFAULT_FRAME_DELTA = 1 / 60
const DEFAULT_PRESENCE_POLL_INTERVAL_MS = 1000
const MIN_KEYBOARD_MAX_SPEED = 1
const KEYBOARD_ACCELERATION_DURATION = 1.0
const NAVIGATION_SCALE_ABSOLUTE_MINIMUM = DEFAULT_NAVIGATION_SCALE_ABSOLUTE_MINIMUM
const WHEEL_MIN_STEP_RATIO = DEFAULT_WHEEL_MIN_STEP_RATIO
const WHEEL_ABSOLUTE_MIN_STEP = DEFAULT_WHEEL_ABSOLUTE_MIN_STEP
const DOUBLE_CLICK_ORTHOGRAPHIC_MIN_ZOOM_FACTOR = 2.5
// A WebGPU pick readback can take longer than a frame on large splat sets.  Keep the world pivot
// for the duration of a short wheel gesture instead of starting a second full GPU sort for every
// wheel event at the same pointer location.
const WHEEL_PICK_REUSE_MS = 120
const WHEEL_GESTURE_IDLE_MS = 180
const WHEEL_PICK_REUSE_DISTANCE_PX = 2
const MIN_TOUCH_PINCH_DISTANCE_PX = 1
const IDENTITY_16 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]
const SPACEMOUSE_ACTIONSET_NODE = 0
const SPACEMOUSE_CATEGORY_NODE = 1
const SPACEMOUSE_ACTION_NODE = 2
const SPACEMOUSE_ROTATE_ACTION_SET_ID = 'mouseRotateMode'
const SPACEMOUSE_ROTATE_CATEGORY_ID = 'mouseRotateMode.category'
const SPACEMOUSE_ROTATE_ARCBALL_COMMAND_ID = 'mouseRotateMode.arcball'
const SPACEMOUSE_ROTATE_YAW_PITCH_COMMAND_ID = 'mouseRotateMode.yawPitch'
const SPACEMOUSE_TARGET_CATEGORY_ID = 'mouseTargetMode.category'
const SPACEMOUSE_TARGET_BOUNDS_CENTER_COMMAND_ID = 'mouseTargetMode.boundsCenter'
const SPACEMOUSE_TARGET_CAPTURED_POINT_COMMAND_ID = 'mouseTargetMode.capturedPoint'
const SPACEMOUSE_HISTORY_CATEGORY_ID = 'appHistory.category'
const SPACEMOUSE_UNDO_COMMAND_ID = 'app.undo'
const SPACEMOUSE_REDO_COMMAND_ID = 'app.redo'
const CAMERA_LOCAL_RIGHT = new THREE.Vector3(1, 0, 0)
const CAMERA_LOCAL_UP = new THREE.Vector3(0, 1, 0)
const CAMERA_LOCAL_FORWARD = new THREE.Vector3(0, 0, -1)
const WORLD_UP = new THREE.Vector3(0, 0, 1)
const WORLD_RIGHT = new THREE.Vector3(1, 0, 0)
const WORLD_FORWARD = new THREE.Vector3(0, 1, 0)
const SCRATCH_V3 = new THREE.Vector3()
const SCRATCH_Q = new THREE.Quaternion()
const SCRATCH_M4 = new THREE.Matrix4()
const SPACEMOUSE_FRONT_VIEW = new THREE.Matrix4()
  .makeBasis(WORLD_RIGHT, WORLD_UP, WORLD_FORWARD.clone().negate())
  .toArray()
const KEYBOARD_TRANSLATION_CODES = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowLeft',
  'ArrowDown',
  'ArrowRight',
  'KeyQ',
  'KeyE',
])

type PointerState = 'none' | 'rotate' | 'pan' | 'touch-dolly-pan'

interface PanGestureAnchor {
  point: THREE.Vector3
  forwardDepth: number
}

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  if (target.isContentEditable) {
    return true
  }

  const tagName = target.tagName
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT'
}

function cloneCameraState(state: CameraState): CameraState {
  return {
    position: state.position.clone(),
    target: state.target.clone(),
    up: state.up.clone(),
    fov: state.fov,
    zoom: state.zoom,
    mode: state.mode,
    roll: state.roll,
  }
}

function vectorEquals(a: THREE.Vector3, b: THREE.Vector3, epsilon = CAMERA_EPSILON): boolean {
  return a.distanceToSquared(b) <= epsilon * epsilon
}

function stateEquals(a: CameraState | null, b: CameraState): boolean {
  if (!a) {
    return false
  }

  return (
    a.mode === b.mode &&
    Math.abs(a.fov - b.fov) <= CAMERA_EPSILON &&
    Math.abs(a.zoom - b.zoom) <= CAMERA_EPSILON &&
    vectorEquals(a.position, b.position) &&
    vectorEquals(a.target, b.target) &&
    vectorEquals(a.up, b.up)
  )
}

/**
 * Unified object-centered controller:
 * - ordinary mouse behaves like the legacy ObjectCenteredControls
 * - a connected 3Dconnexion device can drive the same camera state
 */
export class SpaceMouseController extends Controls<THREE.Camera> implements RenderContributor {
  /** 当前关联的相机实例 */
  declare object: THREE.PerspectiveCamera | THREE.OrthographicCamera
  /** 当前关联的 DOM 元素 */
  declare domElement: HTMLElement | null

  /** 渲染贡献者类型标识 */
  readonly kind = 'spacemouse-overlay'
  /** 通过 ControlPlugin 私有 adapter 提交默认 surface overlay。 */
  readonly graphicsRequirements = CONTROL_GRAPHICS_REQUIREMENTS
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  /** 旋转速度倍率 */
  rotateSpeed = 1
  /** 平移速度倍率 */
  panSpeed = 1
  /** 缩放速度倍率 */
  zoomSpeed = 1

  /** 当前枢轴点世界坐标 */
  readonly pivot = new THREE.Vector3()
  /** 导航目标世界坐标 */
  readonly navTarget = new THREE.Vector3()

  private services: NativeControlServices | null = null
  private readonly surfacePickNearOffset: number | undefined
  private readonly surfacePickWindowSize: number
  private readonly minZoomStep: number
  private readonly perspectiveDeviceCamera = new THREE.PerspectiveCamera()
  private readonly orthographicDeviceCamera = new THREE.OrthographicCamera()
  private deviceCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera =
    this.perspectiveDeviceCamera
  private readonly offset = new THREE.Vector3()
  private readonly rotation = new THREE.Quaternion()
  private readonly lastPointer = new THREE.Vector2()
  private readonly startArcballDir = new THREE.Vector3()
  private readonly startQuat = new THREE.Quaternion()
  private readonly arcballCenterScreen = new THREE.Vector2()
  private readonly lastRight = new THREE.Vector3()
  private readonly lastUp = new THREE.Vector3()
  private readonly lastForward = new THREE.Vector3()
  private readonly arcballDir = new THREE.Vector3()
  private readonly currentMouseScreen = new THREE.Vector2()
  private readonly touchPointers = new Map<number, THREE.Vector2>()
  private readonly touchPinchCenter = new THREE.Vector2()
  private readonly ringGizmo = new THREE.Group()
  private readonly overlayScene = new THREE.Scene()
  private readonly ringMaterials: THREE.MeshBasicMaterial[] = []
  private centerSphereMaterial: THREE.MeshBasicMaterial | null = null
  private readonly lookFrom = new THREE.Vector3()
  private readonly lookDirection = new THREE.Vector3(0, 0, -1)
  private readonly transitionWorldPosition = new THREE.Vector3()
  private readonly transitionFromRotation = new THREE.Quaternion()
  private readonly transitionToRotation = new THREE.Quaternion()
  private readonly keyState = new Set<string>()
  private readonly glState = {
    viewportWidth: 1,
    viewportHeight: 1,
    fov: 60,
    frustumNear: 0.1,
    frustumFar: 1000,
    left: -1,
    right: 1,
    bottom: -1,
    top: 1,
  }

  private nav: _3Dconnexion | null = null
  private navIsActive = false
  private navCreated = false
  private selectionOnly = false
  private pointerState: PointerState = 'none'
  private activePointerId: number | null = null
  private lastCommittedState: CameraState | null = null
  private hasInternalState = false
  private lastStableYaw = 0
  private pointerDragStarted = false
  private panGestureAnchor: PanGestureAnchor | null = null
  private touchPinchDistance = 0
  private pendingRotatePivot: THREE.Vector3 | null = null
  private wheelDelta = 0
  private readonly zoomDelta = new THREE.Vector3()
  private wheelPivot: THREE.Vector3 | null = null
  private wheelPivotPending = false
  private zoomDistance: number | null = null
  private orthographicZoomTarget: number | null = null
  private orthographicZoomAnchorClient: THREE.Vector2 | null = null
  private keyboardMaxSpeed = MIN_KEYBOARD_MAX_SPEED
  private keyboardCurrentSpeed = 0
  private lastWheelPickAt = -Infinity
  private lastWheelPickScreen: THREE.Vector2 | null = null
  private lastWheelPickedPivot: THREE.Vector3 | null = null
  private pickRequestVersion = 0
  private pendingPointerPivotPickVersion = 0
  private pickAbortController: AbortController | null = null

  private boundPointerDown: ((event: PointerEvent) => void) | null = null
  private boundPointerMove: ((event: PointerEvent) => void) | null = null
  private boundPointerUp: ((event: PointerEvent) => void) | null = null
  private boundWheel: ((event: WheelEvent) => void) | null = null
  private boundDoubleClick: ((event: MouseEvent) => void) | null = null
  private boundContextMenu: ((event: MouseEvent) => void) | null = null
  private boundKeyDown: ((event: KeyboardEvent) => void) | null = null
  private boundKeyUp: ((event: KeyboardEvent) => void) | null = null
  private boundBlur: (() => void) | null = null
  private boundDocumentPointerDown: ((event: PointerEvent) => void) | null = null
  private boundDocumentFocusIn: ((event: FocusEvent) => void) | null = null
  private focusRestoreTimer = 0
  private focusRestoreFrame = 0

  private readonly appName: string
  private readonly unitsToMeters: number
  private readonly presenceProvider: SpaceMousePresenceProvider | null
  private readonly presencePollIntervalMs: number
  private interactionElement: HTMLElement | null = null
  private previousInteractionTouchAction: string | null = null
  private lastKnownControlMode: ControlMode = 'none'
  private mouseRotateMode: SpaceMouseMouseRotateMode = 'arcball'
  private mouseTargetMode: SpaceMouseTargetMode = 'bounds-center'
  private mouseNavigationProfile: SpaceMouseNavigationProfile | null = 'hybrid'
  private promoteHybridProfileOnNextConfirmed3DMouseInput = false
  private lastResolved3DMousePresence: boolean | null = null
  private presencePollHandle = 0
  private presenceRequestVersion = 0
  private rotationLocked = false
  private deviceNavigationEnabled = true
  private pivotGizmoEnabled = true
  private appCommandHandlers: SpaceMouseAppCommandHandlers = {}
  private lastCapturedPoint: THREE.Vector3 | null = null
  private rotateModeTransitionEndTime = 0
  private readonly handleControlModeChanged = (payload: {
    mode: ControlMode
    previous: ControlMode
  }): void => {
    this.lastKnownControlMode = payload.mode
  }

  constructor(options: SpaceMouseControllerOptions) {
    const camera = syncViewportCameraFromRig(options.services)
    super(camera, null)

    this.services = options.services
    this.surfacePickNearOffset = options.surfacePicking?.nearOffset
    this.surfacePickWindowSize = resolveNativeControlPickWindowSize(
      options.windowSize,
      'SpaceMouseController.windowSize'
    )
    this.minZoomStep = resolveNativeControlMinZoomStep(
      options.minZoomStep,
      'SpaceMouseController.minZoomStep'
    )
    this.enabled = false
    this.rotateSpeed = options.rotateSpeed ?? 1
    this.panSpeed = options.panSpeed ?? 1
    this.zoomSpeed = options.zoomSpeed ?? 1
    this.appName = options.appName ?? DEFAULT_APP_NAME
    this.unitsToMeters = options.unitsToMeters ?? DEFAULT_UNITS_TO_METERS
    this.presenceProvider = options.presenceProvider ?? null
    this.presencePollIntervalMs = Math.max(
      0,
      options.presencePollIntervalMs ?? DEFAULT_PRESENCE_POLL_INTERVAL_MS
    )
    this.lastKnownControlMode = 'none'
    this.applyNavigationProfile(options.navigationProfile ?? 'hybrid', {
      syncCommandState: false,
      requestFocus: false,
    })

    this.initializePivotRings()
    this.services.events?.on('controls.modeChanged', this.handleControlModeChanged)

    this.syncInternalFromRig(true)
    this.syncDeviceCameraFromRig()
  }

  /** 3Dconnexion 设备是否在主导相机 */
  get isSpaceMouseActive(): boolean {
    return this.navIsActive
  }

  /**
   * 获取当前鼠标旋转模式。
   *
   * @returns 'arcball' 或 'yaw-pitch'
   */
  getMouseRotateMode(): SpaceMouseMouseRotateMode {
    return this.mouseRotateMode
  }

  /**
   * 获取当前鼠标导航预设。
   *
   * @returns 当前导航预设，尚未解析时返回 null
   */
  getNavigationProfile(): SpaceMouseNavigationProfile | null {
    return this.mouseNavigationProfile
  }

  /**
   * 切换鼠标导航预设。
   *
   * @param profile - 目标导航预设
   */
  setNavigationProfile(profile: SpaceMouseNavigationProfile): void {
    this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    this.applyNavigationProfile(profile, {
      syncCommandState: true,
      requestFocus: true,
    })
  }

  /**
   * 切换鼠标旋转模式，必要时启动过渡动画。
   *
   * @param mode - 目标旋转模式
   */
  setMouseRotateMode(mode: SpaceMouseMouseRotateMode): void {
    this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    if (this.mouseRotateMode === mode) {
      return
    }

    if (mode === 'yaw-pitch') {
      this.beginYawPitchTransition()
    } else {
      this.rotateModeTransitionEndTime = 0
    }

    this.mouseRotateMode = mode
    this.syncNavigationProfileFromModes()
    this.sync3DMouseCommandState()
    this.requestFocusRecovery()
  }

  /**
   * 获取当前鼠标旋转 target 模式。
   *
   * @returns 当前 target 模式
   */
  getMouseTargetMode(): SpaceMouseTargetMode {
    return this.mouseTargetMode
  }

  /**
   * 切换鼠标旋转 target 模式。
   *
   * @param mode - 目标 target 模式
   */
  setMouseTargetMode(mode: SpaceMouseTargetMode): void {
    this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    if (this.mouseTargetMode === mode) {
      return
    }

    this.mouseTargetMode = mode
    this.panGestureAnchor = null
    this.syncNavigationProfileFromModes()
    this.sync3DMouseCommandState()
    this.requestFocusRecovery()
  }

  /**
   * 获取鼠标旋转是否被锁定。
   *
   * @returns 旋转锁定状态
   */
  getRotationLocked(): boolean {
    return this.rotationLocked
  }

  /**
   * 切换鼠标旋转锁定状态。
   *
   * @param locked - 是否锁定旋转
   */
  setRotationLocked(locked: boolean): void {
    this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    if (this.rotationLocked === locked) {
      return
    }

    this.rotationLocked = locked
    if (locked) {
      this.rotateModeTransitionEndTime = 0
    }
    this.syncNavigationProfileFromModes()
    this.sync3DMouseCommandState()
    this.requestFocusRecovery()
  }

  /**
   * 获取 3D 鼠标导航是否启用。
   *
   * @returns 3D 鼠标导航启用状态
   */
  get3DMouseNavigationEnabled(): boolean {
    return this.deviceNavigationEnabled
  }

  /**
   * 切换 3D 鼠标导航启用状态。
   *
   * @param enabled - 是否启用 3D 鼠标导航
   */
  set3DMouseNavigationEnabled(enabled: boolean): void {
    if (this.deviceNavigationEnabled === enabled) {
      return
    }

    this.deviceNavigationEnabled = enabled
    if (!enabled) {
      this.navIsActive = false
      this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    }

    this.sync3DMouseCommandState()
    this.requestFocusRecovery()
  }

  /**
   * 获取捕捉点 pivot 环是否可见。
   *
   * @returns pivot 环启用状态
   */
  getPivotGizmoEnabled(): boolean {
    return this.pivotGizmoEnabled
  }

  /**
   * 切换捕捉点 pivot 环可见状态。
   *
   * @param enabled - 是否显示 pivot 环
   */
  setPivotGizmoEnabled(enabled: boolean): void {
    if (this.pivotGizmoEnabled === enabled) {
      return
    }

    this.pivotGizmoEnabled = enabled
    if (!enabled) {
      this.hidePivotRings()
      return
    }

    this.showPivotRings()
  }

  clearCapturedPivot(): void {
    if (this.pointerState !== 'none') {
      return
    }

    this.lastCapturedPoint = null
    this.pendingRotatePivot = null
    if (!this.services) {
      return
    }

    const camera = this.syncManagedCameraFromRig()
    this.pivot.copy(this.resolveBoundsCenterPivot())
    this.syncOffsetFromPosition(camera)
    this.syncNavTargetFromCamera(camera)
    this.updatePivotRings(camera)
    this.sync3DMouseCommandState()
  }

  /**
   * 设置设备按钮触发的应用命令回调。
   *
   * @param handlers - 应用命令回调集合，传空值时清空回调
   */
  setAppCommandHandlers(handlers: SpaceMouseAppCommandHandlers | null | undefined): void {
    this.appCommandHandlers = {
      undo: handlers?.undo,
      redo: handlers?.redo,
    }
  }

  /** 清空设备按钮触发的应用命令回调。 */
  clearAppCommandHandlers(): void {
    this.appCommandHandlers = {}
  }

  /**
   * 连接交互元素并注册设备、鼠标、键盘回调。
   *
   * @param element - 承载交互事件的 HTML 元素
   */
  override connect(element: HTMLElement): void {
    if (!this.services) {
      return
    }

    const interactionElement = this.services.rendererDomElement ?? element
    this.interactionElement = interactionElement
    this.previousInteractionTouchAction = interactionElement.style.touchAction
    interactionElement.style.touchAction = 'none'
    if (!interactionElement.hasAttribute('tabindex')) {
      interactionElement.setAttribute('tabindex', '0')
    }

    super.connect(interactionElement)
    const shouldResetPivot = !this.hasInternalState || this.lastKnownControlMode !== 'spacemouse'
    this.syncInternalFromRig(shouldResetPivot)
    this.syncDeviceCameraFromRig()
    this.showPivotRings()
    this.installMouseHandlers(interactionElement)
    this.promoteHybridProfileOnNextConfirmed3DMouseInput = true
    this.lastResolved3DMousePresence = null
    this.refreshNavigationProfileFromPresence()
    this.startPresencePolling()
    this.connect3DMouse(interactionElement)
    interactionElement.focus()
  }

  /** 断开连接，移除鼠标/设备监听并隐藏枢轴环 */
  override disconnect(): void {
    this.cancelPendingPickRequests()
    this.removeMouseHandlers()
    this.stopPresencePolling()
    this.lastResolved3DMousePresence = null
    this.presenceRequestVersion += 1
    this.disconnect3DMouse()
    if (this.interactionElement && this.previousInteractionTouchAction !== null) {
      this.interactionElement.style.touchAction = this.previousInteractionTouchAction
    }
    this.previousInteractionTouchAction = null
    this.interactionElement = null
    this.hidePivotRings()
    this.stop()
  }

  /** 释放控制器资源 */
  override dispose(): void {
    this.cancelPendingPickRequests()
    this.disconnect()
    this.services?.events?.off('controls.modeChanged', this.handleControlModeChanged)
    this.disposePivotRings()
  }

  /**
   * 每帧更新控制器状态。
   *
   * @param deltaTime - 自上一帧的时间间隔，单位秒
   */
  override update(deltaTime?: number): void {
    if (!this.enabled || !this.services) {
      return
    }

    const camera = this.syncManagedCameraFromRig()
    if (this.services.rig.isTransitioning) {
      camera.updateMatrixWorld(true)
      this.updatePivotRings(camera)
      this.syncDeviceCameraFromRig()
      return
    }

    if (this.shouldSyncInternalFromRig()) {
      const state = this.services.rig.getState()
      const shouldResetPivot =
        !this.hasInternalState || this.shouldRefreshBoundsCenterPivotFromRig()
      this.syncInternalFromCamera(camera, shouldResetPivot)
      if (!shouldResetPivot) {
        this.navTarget.copy(state.target)
      }
      this.resetKeyboardMotionProfile(state.position.distanceTo(state.target))
    }
    this.stepRotateModeTransition()

    if (this.nav && this.navIsActive) {
      this.syncDeviceCameraFromRig()
      this.updatePivotRings(camera)
      this.nav.update3dcontroller({
        frame: {
          time: performance.now(),
        },
      })
      return
    }

    if (this.pointerState === 'none') {
      this.updateLegacyWheelZoom()
      this.updateOrthographicZoomAnimation(camera, deltaTime)
      this.updateKeyboardTranslation(deltaTime)
    }
    this.applyStateToCamera(camera)
    this.updatePivotRings(camera)
    this.commitCameraToRig(camera)
    this.syncDeviceCameraFromRig()
  }

  /**
   * 渲染枢轴环到当前视图。
   *
   * @param frame - 帧上下文
   * @param _phase - 当前渲染阶段
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    if (
      !this.enabled ||
      !this.ringGizmo.visible ||
      (this.services?.viewId != null && ctx.viewId !== this.services.viewId)
    ) {
      return
    }

    controlOverlayGraphicsAdapter.render(this.overlayScene, frame.camera, ctx)
  }

  /** 停止所有运动累积，清空输入状态 */
  stop(): void {
    if (this.interactionElement) {
      this.releaseTouchPointerCaptures(this.interactionElement)
    }
    this.pointerState = 'none'
    this.activePointerId = null
    this.pointerDragStarted = false
    this.panGestureAnchor = null
    this.pendingRotatePivot = null
    this.resetTouchGesture()
    this.navIsActive = false
    this.clearWheelZoomState()
    this.keyState.clear()
    this.keyboardCurrentSpeed = 0
  }

  private syncManagedCameraFromRig(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    if (!this.services) {
      return this.object as THREE.PerspectiveCamera | THREE.OrthographicCamera
    }

    const camera = syncViewportCameraFromRig(this.services)
    if (this.object !== camera) {
      this.object = camera
    }
    this.updateViewportMetrics()
    return camera
  }

  private syncInternalFromRig(resetPivot: boolean): void {
    const camera = this.syncManagedCameraFromRig()
    const state = this.services!.rig.getState()
    this.syncInternalFromCamera(camera, resetPivot || this.shouldRefreshBoundsCenterPivotFromRig())
    this.navTarget.copy(state.target)
    this.resetKeyboardMotionProfile(state.position.distanceTo(state.target))
    this.lastCommittedState = cloneCameraState(state)
  }

  private syncInternalFromCamera(camera: THREE.Camera, resetPivot: boolean): void {
    if (resetPivot || !this.hasInternalState) {
      this.pivot.copy(this.getEffectiveTargetPivot())
      this.syncNavTargetToEffectivePivot()
    }

    this.rotation.copy(camera.quaternion)
    this.syncOffsetFromPosition(camera)
    this.refreshAxesFromQuat()
    this.getStableYaw(this.lastForward)
    this.hasInternalState = true

    if (this.pointerState === 'rotate') {
      this.startQuat.copy(this.rotation)
      this.startArcballDir.copy(this.mouseToArcballDir(this.lastPointer.x, this.lastPointer.y))
    }

    this.updatePivotRings(camera)
  }

  private resolveBoundsCenterPivot(): THREE.Vector3 {
    if (!this.services) {
      return new THREE.Vector3()
    }

    const bounds = this.services.getSceneBounds?.('fit') ?? this.services.getSceneBounds?.()
    if (bounds && !bounds.isEmpty()) {
      return bounds.getCenter(new THREE.Vector3())
    }

    if (this.hasInternalState) {
      return this.pivot.clone()
    }

    return this.services.rig.target
  }

  private shouldRefreshBoundsCenterPivotFromRig(): boolean {
    return (
      this.hasInternalState &&
      this.pointerState === 'none' &&
      !this.navIsActive &&
      this.mouseTargetMode === 'bounds-center'
    )
  }

  private getEffectiveTargetPivot(): THREE.Vector3 {
    if (this.mouseTargetMode === 'captured-point' && this.lastCapturedPoint) {
      return this.lastCapturedPoint.clone()
    }

    return this.resolveBoundsCenterPivot()
  }

  private syncNavTargetToEffectivePivot(): void {
    this.navTarget.copy(this.getEffectiveTargetPivot())
  }

  private resolveNavigationProfileFromModes(): SpaceMouseNavigationProfile | null {
    if (this.mouseRotateMode === 'yaw-pitch' && this.mouseTargetMode === 'captured-point') {
      return 'mouse-only'
    }

    if (this.mouseRotateMode === 'arcball' && this.mouseTargetMode === 'bounds-center') {
      return 'hybrid'
    }

    return null
  }

  private syncNavigationProfileFromModes(): void {
    this.mouseNavigationProfile = this.resolveNavigationProfileFromModes()
    this.emitNavigationStateChanged()
  }

  private applyNavigationProfile(
    profile: SpaceMouseNavigationProfile,
    options: {
      syncCommandState: boolean
      requestFocus: boolean
    }
  ): void {
    const nextState =
      profile === 'mouse-only'
        ? {
            rotateMode: 'yaw-pitch' as SpaceMouseMouseRotateMode,
            targetMode: 'captured-point' as SpaceMouseTargetMode,
          }
        : {
            rotateMode: 'arcball' as SpaceMouseMouseRotateMode,
            targetMode: 'bounds-center' as SpaceMouseTargetMode,
          }
    const rotateModeChanged = this.mouseRotateMode !== nextState.rotateMode

    if (rotateModeChanged) {
      if (nextState.rotateMode === 'yaw-pitch') {
        this.beginYawPitchTransition()
      } else {
        this.rotateModeTransitionEndTime = 0
      }
    }

    this.mouseRotateMode = nextState.rotateMode
    this.mouseTargetMode = nextState.targetMode
    this.mouseNavigationProfile = profile
    this.emitNavigationStateChanged()

    if (options.syncCommandState) {
      this.sync3DMouseCommandState()
    }

    if (options.requestFocus) {
      this.requestFocusRecovery()
    }
  }

  private startPresencePolling(): void {
    if (!this.presenceProvider || this.presencePollIntervalMs <= 0) {
      return
    }

    this.stopPresencePolling()
    this.presencePollHandle = window.setInterval(() => {
      this.refreshNavigationProfileFromPresence()
    }, this.presencePollIntervalMs)
  }

  private stopPresencePolling(): void {
    if (this.presencePollHandle === 0) {
      return
    }

    window.clearInterval(this.presencePollHandle)
    this.presencePollHandle = 0
  }

  private refreshNavigationProfileFromPresence(): void {
    if (!this.presenceProvider) {
      return
    }

    const requestVersion = ++this.presenceRequestVersion

    try {
      const result = this.presenceProvider()
      if (typeof result === 'boolean') {
        this.applyDetected3DMousePresence(result, requestVersion)
        return
      }

      void Promise.resolve(result)
        .then((present) => {
          this.applyDetected3DMousePresence(Boolean(present), requestVersion)
        })
        .catch(() => {})
    } catch {}
  }

  private isPresetActive(profile: SpaceMouseNavigationProfile): boolean {
    if (profile === 'mouse-only') {
      return (
        this.mouseNavigationProfile === 'mouse-only' &&
        this.mouseRotateMode === 'yaw-pitch' &&
        this.mouseTargetMode === 'captured-point'
      )
    }

    return (
      this.mouseNavigationProfile === 'hybrid' &&
      this.mouseRotateMode === 'arcball' &&
      this.mouseTargetMode === 'bounds-center'
    )
  }

  private applyDetected3DMousePresence(present: boolean, requestVersion: number): void {
    if (requestVersion !== this.presenceRequestVersion || !this.interactionElement) {
      return
    }

    const previousPresence = this.lastResolved3DMousePresence
    this.lastResolved3DMousePresence = present
    this.promoteHybridProfileOnNextConfirmed3DMouseInput = !present

    if (previousPresence === present) {
      return
    }

    const targetProfile: SpaceMouseNavigationProfile = present ? 'hybrid' : 'mouse-only'
    if (this.isPresetActive(targetProfile)) {
      return
    }

    this.applyNavigationProfile(targetProfile, {
      syncCommandState: true,
      requestFocus: false,
    })
  }

  private promoteNavigationProfileForConfirmed3DMouseInput(): void {
    if (!this.promoteHybridProfileOnNextConfirmed3DMouseInput) {
      return
    }

    this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    if (
      this.mouseNavigationProfile === 'hybrid' &&
      this.mouseRotateMode === 'arcball' &&
      this.mouseTargetMode === 'bounds-center' &&
      !this.rotationLocked
    ) {
      return
    }

    this.applyNavigationProfile('hybrid', {
      syncCommandState: false,
      requestFocus: false,
    })
  }

  private emitNavigationStateChanged(): void {
    this.services?.events?.emit('controls.spacemouseNavigationChanged', {
      viewId: this.services?.viewId ?? null,
      profile: this.mouseNavigationProfile,
      rotateMode: this.mouseRotateMode,
      targetMode: this.mouseTargetMode,
      rotationLocked: this.rotationLocked,
    })
  }

  private pickSurfaceHitAtScreen(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    interactionType: 'click' | 'move' = 'move'
  ): NativeControlSurfaceQueryHit | null {
    if (!this.services?.surfaceQuery || !this.domElement) {
      return null
    }

    const { width, height } = this.services.getViewportSize()
    if (width <= 0 || height <= 0) {
      return null
    }

    if (screen.x < 0 || screen.y < 0 || screen.x > width || screen.y > height) {
      return null
    }

    const rect = this.domElement.getBoundingClientRect()
    const screenInWindow = screen.clone().add(new THREE.Vector2(rect.left, rect.top))
    const minDistance = this.resolveSurfacePickMinDistance(screenInWindow, camera)
    if (minDistance === null) {
      return null
    }
    return this.services.surfaceQuery.queryAtScreen(screenInWindow, camera, {
      kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
      interactionType,
      interactionSource: 'spacemouse',
      windowSize: this.surfacePickWindowSize,
      centerSphereRadius: interactionType === 'move' ? DEFAULT_PICK_RADIUS : undefined,
      ...(minDistance === undefined ? {} : { minDistance }),
    })
  }

  private pickSurfaceHitAtScreenAsync(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    interactionType: 'click' | 'move',
    signal: AbortSignal
  ): Promise<NativeControlSurfaceQueryHit | null> {
    if (!this.services?.surfaceQuery?.queryAtScreenAsync || !this.domElement) {
      return Promise.resolve(null)
    }

    const rect = this.domElement.getBoundingClientRect()
    const screenInWindow = screen.clone().add(new THREE.Vector2(rect.left, rect.top))
    const minDistance = this.resolveSurfacePickMinDistance(screenInWindow, camera)
    if (minDistance === null) {
      return Promise.resolve(null)
    }
    return this.services.surfaceQuery.queryAtScreenAsync(screenInWindow, camera, {
      kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
      interactionType,
      interactionSource: 'spacemouse',
      windowSize: this.surfacePickWindowSize,
      centerSphereRadius: interactionType === 'move' ? DEFAULT_PICK_RADIUS : undefined,
      signal,
      ...(minDistance === undefined ? {} : { minDistance }),
    })
  }

  private resolveSurfacePickMinDistance(
    screen: THREE.Vector2,
    camera: THREE.Camera
  ): number | null | undefined {
    if (!this.domElement) {
      return this.surfacePickNearOffset === undefined ? undefined : null
    }
    const rect = this.domElement.getBoundingClientRect()
    return resolveNativeControlPickMinDistanceAtScreen(
      camera,
      screen,
      { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
      this.surfacePickNearOffset
    )
  }

  private schedulePointerPivotPick(
    event: PointerEvent,
    camera: THREE.Camera,
    interactionType: 'click' | 'move'
  ): void {
    const version = ++this.pickRequestVersion
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    this.pendingPointerPivotPickVersion = version
    this.hidePivotRings()
    void this.pickSurfaceHitAtPointerAsync(event, camera, interactionType, controller.signal)
      .then((hit) => {
        if (
          !hit ||
          version !== this.pickRequestVersion ||
          version !== this.pendingPointerPivotPickVersion ||
          !isCurrentViewportCamera(this.services!, camera)
        ) {
          return
        }

        const pivot = hit.point.clone()
        this.applyResolvedPointerPivot(pivot, event, camera)
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
          this.pendingPointerPivotPickVersion = 0
          if (this.enabled) {
            this.updatePivotRings(this.syncManagedCameraFromRig())
          }
        }
      })
  }

  private scheduleDoubleClickPick(event: MouseEvent, camera: THREE.Camera): void {
    const version = ++this.pickRequestVersion
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    void this.pickSurfaceHitAtPointerAsync(event, camera, 'click', controller.signal)
      .then((hit) => {
        if (
          !hit ||
          version !== this.pickRequestVersion ||
          !this.enabled ||
          !isCurrentViewportCamera(this.services!, camera)
        )
          return
        this.applyDoubleClickHit(event, camera, hit)
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private pickSurfaceHitAtPointerAsync(
    event: MouseEvent | PointerEvent | WheelEvent,
    camera: THREE.Camera,
    interactionType: 'click' | 'move',
    signal: AbortSignal
  ): Promise<NativeControlSurfaceQueryHit | null> {
    if (!this.domElement) return Promise.resolve(null)
    const rect = this.domElement.getBoundingClientRect()
    return this.pickSurfaceHitAtScreenAsync(
      new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top),
      camera,
      interactionType,
      signal
    )
  }

  private applyDoubleClickHit(
    event: MouseEvent,
    camera: THREE.Camera,
    hit: NativeControlSurfaceQueryHit
  ): void {
    if (camera instanceof THREE.OrthographicCamera) {
      this.clearWheelZoomState()
      if (!this.startOrthographicDoubleClickZoom(event, camera, hit)) return
      if (!this.updateOrthographicZoomAnimation(camera, DEFAULT_FRAME_DELTA)) return
      this.updatePivotRings(camera)
      this.commitCameraToRig(camera)
      this.syncDeviceCameraFromRig()
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    if (!(camera instanceof THREE.PerspectiveCamera)) return

    const pivot = hit.point
    const current = this.getCurrentViewState()
    const navigationScale = this.resolveWheelNavigationScale(current.distance)
    const wheelDelta = resolveDoubleClickWheelDelta({
      position: current.position,
      pickedPoint: pivot,
      hit,
      proportionalStepRatio: LEGACY_WHEEL_STEP_RATIO,
      navigationScale,
      minStepRatio: WHEEL_MIN_STEP_RATIO,
      absoluteMinStep: this.minZoomStep,
    })
    if (wheelDelta <= 0) return
    this.wheelPivot = pivot.clone()
    this.wheelDelta += wheelDelta
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  private syncOffsetFromPosition(camera: THREE.Camera): void {
    SCRATCH_V3.subVectors(camera.position, this.pivot)
    this.offset.copy(SCRATCH_V3.applyQuaternion(SCRATCH_Q.copy(this.rotation).invert()))
  }

  private syncOffsetFromWorldPosition(position: THREE.Vector3): void {
    SCRATCH_V3.subVectors(position, this.pivot)
    this.offset.copy(SCRATCH_V3.applyQuaternion(SCRATCH_Q.copy(this.rotation).invert()))
  }

  private refreshAxesFromQuat(): void {
    this.lastRight.copy(CAMERA_LOCAL_RIGHT).applyQuaternion(this.rotation)
    this.lastUp.copy(CAMERA_LOCAL_UP).applyQuaternion(this.rotation)
    this.lastForward.copy(CAMERA_LOCAL_FORWARD).applyQuaternion(this.rotation)
  }

  private getCurrentViewState(): {
    position: THREE.Vector3
    forward: THREE.Vector3
    target: THREE.Vector3
    distance: number
  } {
    const position = this.pivot
      .clone()
      .add(SCRATCH_V3.copy(this.offset).applyQuaternion(this.rotation))
    const forward = CAMERA_LOCAL_FORWARD.clone().applyQuaternion(this.rotation).normalize()
    const target = this.navTarget.clone()
    const distance = Math.max(position.distanceTo(target), 0.1)
    return { position, forward, target, distance }
  }

  private applyWheelCameraState(
    position: THREE.Vector3,
    target: THREE.Vector3,
    options: { preserveRotationPivot?: boolean } = {}
  ): void {
    this.navTarget.copy(target)
    this.pivot.copy(options.preserveRotationPivot ? this.getEffectiveTargetPivot() : target)
    this.syncOffsetFromWorldPosition(position)
    this.refreshAxesFromQuat()
  }

  private translateView(
    movement: THREE.Vector3,
    options: {
      includeCapturedPoint?: boolean
    } = {}
  ): void {
    if (movement.lengthSq() <= 1e-12) {
      return
    }

    this.pivot.add(movement)
    this.navTarget.add(movement)
    if (options.includeCapturedPoint && this.lastCapturedPoint) {
      this.lastCapturedPoint.add(movement)
    }
  }

  private syncInteractiveViewState(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera = this.syncManagedCameraFromRig()
  ): void {
    this.applyStateToCamera(camera)
    this.syncNavTargetFromCamera(camera)
    this.updatePivotRings(camera)
    this.commitCameraToRig(camera)
    this.syncDeviceCameraFromRig()
  }

  private syncNavTargetFromCamera(camera: THREE.Camera): void {
    const forward = CAMERA_LOCAL_FORWARD.clone().applyQuaternion(camera.quaternion).normalize()
    const distance = Math.max(camera.position.distanceTo(this.navTarget), 0.1)
    this.navTarget.copy(camera.position.clone().addScaledVector(forward, distance))
  }

  private applyOrthographicWheelZoom(event: WheelEvent, camera: THREE.OrthographicCamera): boolean {
    if (!this.domElement || this.zoomSpeed === 0) {
      return false
    }

    const rect = this.domElement.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) {
      return false
    }

    const normalizedDelta = Math.abs(event.deltaY * 0.01)
    if (normalizedDelta <= 0) {
      return false
    }

    const dollyScale = 0.95 ** (this.zoomSpeed * normalizedDelta)
    if (!Number.isFinite(dollyScale) || dollyScale <= 0) {
      return false
    }

    const previousZoom = camera.zoom

    const nextZoom = event.deltaY < 0 ? camera.zoom / dollyScale : camera.zoom * dollyScale
    if (!this.applyOrthographicZoomAtClientPoint(camera, event.clientX, event.clientY, nextZoom)) {
      return false
    }

    return Math.abs(camera.zoom - previousZoom) > CAMERA_EPSILON
  }

  private applyOrthographicZoomAtClientPoint(
    camera: THREE.OrthographicCamera,
    clientX: number,
    clientY: number,
    zoom: number
  ): boolean {
    if (!this.domElement) {
      return false
    }

    const rect = this.domElement.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) {
      return false
    }

    const mouseX = ((clientX - rect.left) / rect.width) * 2 - 1
    const mouseY = -((clientY - rect.top) / rect.height) * 2 + 1
    const mouseBefore = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera)
    const previousZoom = camera.zoom

    camera.zoom = Math.max(CAMERA_EPSILON, zoom)
    if (Math.abs(camera.zoom - previousZoom) <= CAMERA_EPSILON) {
      return false
    }

    camera.updateProjectionMatrix()
    const mouseAfter = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera)
    const translation = mouseBefore.sub(mouseAfter)

    camera.position.add(translation)
    camera.updateMatrixWorld(true)

    this.pivot.add(translation)
    this.navTarget.add(translation)
    this.syncOffsetFromPosition(camera)
    this.refreshAxesFromQuat()
    return true
  }

  private updateOrthographicZoomAnimation(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    deltaTime?: number
  ): boolean {
    if (
      !(camera instanceof THREE.OrthographicCamera) ||
      this.orthographicZoomTarget == null ||
      !this.orthographicZoomAnchorClient
    ) {
      return false
    }

    const targetZoom = this.orthographicZoomTarget
    const currentZoom = camera.zoom
    const remaining = targetZoom - currentZoom
    if (remaining <= CAMERA_EPSILON) {
      this.clearOrthographicZoomAnimation()
      return false
    }

    const safeDelta =
      deltaTime && Number.isFinite(deltaTime) && deltaTime > 0 ? deltaTime : DEFAULT_FRAME_DELTA
    const fade = 0.5 ** (LEGACY_WHEEL_FADE_FACTOR * safeDelta)
    const progression = 1 - fade
    const nextZoom = Math.min(targetZoom, currentZoom + remaining * progression)

    const changed = this.applyOrthographicZoomAtClientPoint(
      camera,
      this.orthographicZoomAnchorClient.x,
      this.orthographicZoomAnchorClient.y,
      nextZoom
    )
    if (!changed) {
      this.clearOrthographicZoomAnimation()
      return false
    }

    if (targetZoom - camera.zoom <= CAMERA_EPSILON) {
      this.clearOrthographicZoomAnimation()
    }
    return true
  }

  private clearOrthographicZoomAnimation(): void {
    this.orthographicZoomTarget = null
    this.orthographicZoomAnchorClient = null
  }

  private startOrthographicDoubleClickZoom(
    event: MouseEvent,
    camera: THREE.OrthographicCamera,
    hit: NativeControlSurfaceQueryHit
  ): boolean {
    const current = this.getCurrentViewState()
    const navigationScale = this.resolveWheelNavigationScale(current.distance)
    const targetRadius = resolveDoubleClickTargetRadius(
      hit,
      navigationScale,
      WHEEL_MIN_STEP_RATIO,
      WHEEL_ABSOLUTE_MIN_STEP
    )
    const targetHalfHeight = Math.max(
      targetRadius / Math.tan(THREE.MathUtils.degToRad(this.services?.rig.fov ?? 60) / 2),
      CAMERA_EPSILON
    )
    const targetZoom = Math.max(
      camera.top / targetHalfHeight,
      camera.zoom * DOUBLE_CLICK_ORTHOGRAPHIC_MIN_ZOOM_FACTOR
    )
    if (targetZoom <= camera.zoom + CAMERA_EPSILON) {
      return false
    }

    this.orthographicZoomTarget = targetZoom
    this.orthographicZoomAnchorClient = new THREE.Vector2(event.clientX, event.clientY)
    return true
  }

  private beginRotateGesture(camera: THREE.Camera): void {
    const nextPivot =
      this.mouseTargetMode === 'captured-point' && this.pendingRotatePivot
        ? this.pendingRotatePivot.clone()
        : this.getEffectiveTargetPivot()
    if (this.mouseTargetMode === 'captured-point' && this.pendingRotatePivot) {
      this.lastCapturedPoint = this.pendingRotatePivot.clone()
    }

    this.pivot.copy(nextPivot)
    this.syncOffsetFromPosition(camera)
    this.syncNavTargetFromCamera(camera)
    if (this.mouseTargetMode === 'captured-point' && this.pendingRotatePivot) {
      this.emitNavigationStateChanged()
    }

    this.arcballCenterScreen.copy(this.pivotToScreen())
    this.refreshAxesFromQuat()
    this.startArcballDir.copy(this.mouseToArcballDir(this.lastPointer.x, this.lastPointer.y))
    this.startQuat.copy(this.rotation)
    this.pendingRotatePivot = null
  }

  private setPanGestureAnchor(point: THREE.Vector3, camera: THREE.Camera): boolean {
    const cameraForward = CAMERA_LOCAL_FORWARD.clone()
      .applyQuaternion(camera.quaternion)
      .normalize()
    const forwardDepth = point.clone().sub(camera.position).dot(cameraForward)
    if (!Number.isFinite(forwardDepth) || forwardDepth <= CAMERA_EPSILON) {
      return false
    }

    this.panGestureAnchor = {
      point: point.clone(),
      forwardDepth,
    }
    return true
  }

  private beginPanGesture(event: PointerEvent, camera: THREE.Camera): void {
    let capturedPivot: THREE.Vector3 | null = null
    if (this.mouseTargetMode === 'captured-point') {
      capturedPivot = shouldUseAsyncPicking(this.services)
        ? (this.lastCapturedPoint?.clone() ?? null)
        : this.pickPivotAtPointer(event, camera, 'click')
    }

    if (capturedPivot) {
      const hasCapturedDepth = this.setPanGestureAnchor(capturedPivot, camera)
      this.lastCapturedPoint = capturedPivot.clone()
      this.pivot.copy(capturedPivot)
      this.syncOffsetFromPosition(camera)
      this.syncNavTargetFromCamera(camera)
      this.emitNavigationStateChanged()
      if (hasCapturedDepth) {
        return
      }
    }

    if (this.mouseTargetMode === 'captured-point' && this.setPanGestureAnchor(this.pivot, camera)) {
      return
    }

    const current = this.getCurrentViewState()
    if (this.setPanGestureAnchor(current.target, camera)) {
      return
    }

    const cameraForward = CAMERA_LOCAL_FORWARD.clone()
      .applyQuaternion(camera.quaternion)
      .normalize()
    const fallbackDepth = this.resolvePointerPanNavigationScale(current.distance)
    this.panGestureAnchor = {
      point: camera.position.clone().addScaledVector(cameraForward, fallbackDepth),
      forwardDepth: fallbackDepth,
    }
  }

  private panByScreenDelta(camera: THREE.Camera, dx: number, dy: number): boolean {
    if (dx === 0 && dy === 0) {
      return false
    }

    const width = Math.max(this.domElement?.clientWidth ?? 1, 1)
    const height = Math.max(this.domElement?.clientHeight ?? 1, 1)
    const current = this.getCurrentViewState()
    let panX = 0
    let panY = 0

    if (camera instanceof THREE.PerspectiveCamera) {
      const panDepth = this.resolvePointerPanNavigationScale(
        this.panGestureAnchor?.forwardDepth ?? current.distance
      )
      const targetDistance = panDepth * Math.tan((camera.fov * Math.PI) / 360)
      panX = (-2 * dx * targetDistance * this.panSpeed) / height
      panY = (2 * dy * targetDistance * this.panSpeed) / height
    } else if (camera instanceof THREE.OrthographicCamera) {
      panX =
        (-dx * (camera.right - camera.left) * this.panSpeed) /
        Math.max(camera.zoom, CAMERA_EPSILON) /
        width
      panY =
        (dy * (camera.top - camera.bottom) * this.panSpeed) /
        Math.max(camera.zoom, CAMERA_EPSILON) /
        height
    } else {
      return false
    }

    this.offset.x += panX
    this.offset.y += panY
    this.navTarget.addScaledVector(this.lastRight, panX).addScaledVector(this.lastUp, panY)
    return Math.abs(panX) > CAMERA_EPSILON || Math.abs(panY) > CAMERA_EPSILON
  }

  private beginTouchDollyPan(): void {
    const gesture = this.getTouchGestureMetrics()
    if (!gesture) {
      return
    }

    this.cancelPendingPickRequests()
    this.clearWheelZoomState()
    this.pointerState = 'touch-dolly-pan'
    this.activePointerId = null
    this.pointerDragStarted = false
    this.panGestureAnchor = null
    this.pendingRotatePivot = null
    this.touchPinchDistance = gesture.distance
    this.touchPinchCenter.copy(gesture.center)
  }

  private updateTouchDollyPan(): void {
    const gesture = this.getTouchGestureMetrics()
    if (!gesture) {
      return
    }

    if (this.touchPinchDistance < MIN_TOUCH_PINCH_DISTANCE_PX) {
      this.touchPinchDistance = gesture.distance
      this.touchPinchCenter.copy(gesture.center)
      return
    }

    const camera = this.syncManagedCameraFromRig()
    const dollyScale = (gesture.distance / this.touchPinchDistance) ** this.zoomSpeed
    const panDeltaX = gesture.center.x - this.touchPinchCenter.x
    const panDeltaY = gesture.center.y - this.touchPinchCenter.y
    let didMutateView = this.dollyByScale(camera, dollyScale)

    if (panDeltaX !== 0 || panDeltaY !== 0) {
      didMutateView = this.panByScreenDelta(camera, panDeltaX, panDeltaY) || didMutateView
    }

    this.touchPinchDistance = gesture.distance
    this.touchPinchCenter.copy(gesture.center)
    if (!didMutateView) {
      return
    }

    this.syncInteractiveViewState(camera)
  }

  private getTouchGestureMetrics(): { distance: number; center: THREE.Vector2 } | null {
    const [first, second] = this.touchPointers.values()
    if (!first || !second) {
      return null
    }

    const distance = first.distanceTo(second)
    if (!Number.isFinite(distance)) {
      return null
    }

    return {
      distance,
      center: first.clone().add(second).multiplyScalar(0.5),
    }
  }

  private dollyByScale(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    dollyScale: number
  ): boolean {
    if (!Number.isFinite(dollyScale) || dollyScale <= 0 || this.zoomSpeed === 0) {
      return false
    }

    if (camera instanceof THREE.OrthographicCamera) {
      const previousZoom = camera.zoom
      camera.zoom = Math.max(CAMERA_EPSILON, previousZoom * dollyScale)
      if (Math.abs(camera.zoom - previousZoom) <= CAMERA_EPSILON) {
        return false
      }
      camera.updateProjectionMatrix()
      return true
    }

    const current = this.getCurrentViewState()
    const nextDistance = Math.max(CAMERA_EPSILON, current.distance / dollyScale)
    if (Math.abs(nextDistance - current.distance) <= CAMERA_EPSILON) {
      return false
    }

    const nextPosition = current.target
      .clone()
      .addScaledVector(current.position.clone().sub(current.target).normalize(), nextDistance)
    this.applyWheelCameraState(nextPosition, current.target, {
      preserveRotationPivot:
        this.mouseTargetMode === 'captured-point' && this.lastCapturedPoint !== null,
    })
    return true
  }

  private rebaseRemainingTouchPointer(): void {
    const remaining = this.touchPointers.entries().next().value as
      | [number, THREE.Vector2]
      | undefined
    this.touchPinchDistance = 0
    this.touchPinchCenter.set(0, 0)
    this.pointerDragStarted = false
    this.panGestureAnchor = null
    this.pendingRotatePivot = null

    if (!remaining) {
      this.pointerState = 'none'
      this.activePointerId = null
      return
    }

    const [pointerId, position] = remaining
    this.pointerState = 'rotate'
    this.activePointerId = pointerId
    this.lastPointer.copy(position)
    this.startQuat.copy(this.rotation)
    this.startArcballDir.copy(this.mouseToArcballDir(position.x, position.y))
  }

  private releaseTouchPointerCaptures(element: HTMLElement): void {
    if (typeof element.releasePointerCapture !== 'function') {
      return
    }
    for (const pointerId of this.touchPointers.keys()) {
      element.releasePointerCapture(pointerId)
    }
  }

  private resetTouchGesture(): void {
    const hadTouchPointers = this.touchPointers.size > 0
    this.touchPointers.clear()
    this.touchPinchDistance = 0
    this.touchPinchCenter.set(0, 0)
    if (hadTouchPointers || this.pointerState === 'touch-dolly-pan') {
      this.pointerState = 'none'
      this.activePointerId = null
      this.pointerDragStarted = false
      this.panGestureAnchor = null
      this.pendingRotatePivot = null
    }
  }

  private applyResolvedPointerPivot(
    pivot: THREE.Vector3,
    event: PointerEvent,
    camera: THREE.Camera
  ): void {
    if (this.mouseTargetMode !== 'captured-point' || !this.enabled) {
      return
    }

    const pointerStillActive = this.activePointerId === event.pointerId
    if (pointerStillActive && this.pointerState === 'rotate' && !this.rotationLocked) {
      if (!this.pointerDragStarted) {
        // The first drag event will consume this pivot in beginRotateGesture.
        this.pendingRotatePivot = pivot
        return
      }

      // The gesture already started with the fallback pivot. Rebase its baseline without
      // changing the current camera pose, so subsequent motion rotates around the real hit.
      this.pendingRotatePivot = pivot
      this.beginRotateGesture(camera)
      this.updatePivotRings(camera)
      this.syncInteractiveViewState()
      return
    }

    if (pointerStillActive && this.pointerState !== 'pan') {
      return
    }

    if (pointerStillActive && this.pointerState === 'pan' && !this.pointerDragStarted) {
      this.setPanGestureAnchor(pivot, camera)
    }

    // The pointer may have been released before the async readback completed. Keep the hit as
    // the captured target for the next gesture while preserving the current camera pose.
    this.lastCapturedPoint = pivot.clone()
    this.pivot.copy(pivot)
    this.syncOffsetFromPosition(camera)
    this.syncNavTargetFromCamera(camera)
    this.updatePivotRings(camera)
    this.sync3DMouseCommandState()
    this.emitNavigationStateChanged()
  }

  private applyStateToCamera(camera: THREE.Camera): void {
    camera.position
      .copy(this.pivot)
      .add(SCRATCH_V3.copy(this.offset).applyQuaternion(this.rotation))
    camera.quaternion.copy(this.rotation)
    camera.up.copy(CAMERA_LOCAL_UP).applyQuaternion(this.rotation)
    camera.updateMatrixWorld(true)
  }

  private commitCameraToRig(camera: THREE.Camera): void {
    if (!this.services) {
      return
    }

    const previous = this.services.rig.getState()
    const position = camera.position.clone()
    const up = CAMERA_LOCAL_UP.clone().applyQuaternion(camera.quaternion).normalize()
    const target = this.navTarget.clone()

    const nextState: CameraState = {
      ...previous,
      position,
      target,
      up,
      fov: camera instanceof THREE.PerspectiveCamera ? camera.fov : previous.fov,
      zoom: camera instanceof THREE.OrthographicCamera ? camera.zoom : previous.zoom,
      mode:
        camera instanceof THREE.OrthographicCamera
          ? CameraMode.Orthographic
          : CameraMode.Perspective,
    }

    this.services.rig.setState(nextState)
    this.lastCommittedState = cloneCameraState(nextState)
  }

  private shouldSyncInternalFromRig(): boolean {
    if (!this.services) {
      return false
    }

    const state = this.services.rig.getState()
    return !stateEquals(this.lastCommittedState, state)
  }

  private installMouseHandlers(element: HTMLElement): void {
    this.removeMouseHandlers()

    this.boundPointerDown = (event: PointerEvent) => {
      if (!this.enabled || (event.button !== 0 && event.button !== 2)) {
        return
      }

      if (event.pointerType === 'touch') {
        if (this.touchPointers.size >= 2 || this.touchPointers.has(event.pointerId)) {
          event.preventDefault()
          return
        }

        this.touchPointers.set(event.pointerId, new THREE.Vector2(event.clientX, event.clientY))
        if (this.touchPointers.size === 2) {
          if (typeof element.setPointerCapture === 'function') {
            element.setPointerCapture(event.pointerId)
          }
          this.beginTouchDollyPan()
          event.preventDefault()
          return
        }
      }

      this.interactionElement?.focus()
      this.updateCurrentMouseScreen(event)
      this.clearWheelZoomState()
      this.activePointerId = event.pointerId
      this.pointerState = event.button === 0 ? 'rotate' : 'pan'
      this.pointerDragStarted = false
      this.lastPointer.set(event.clientX, event.clientY)

      const camera = this.syncManagedCameraFromRig()
      camera.updateMatrixWorld(true)

      if (event.button === 0) {
        this.pendingRotatePivot =
          !shouldUseAsyncPicking(this.services) &&
          !this.rotationLocked &&
          this.mouseTargetMode === 'captured-point'
            ? this.pickPivotAtPointer(event, camera, 'click')
            : null
        if (
          shouldUseAsyncPicking(this.services) &&
          !this.rotationLocked &&
          this.mouseTargetMode === 'captured-point'
        ) {
          this.schedulePointerPivotPick(event, camera, 'click')
        }
        if (!this.rotationLocked && this.mouseRotateMode === 'yaw-pitch') {
          this.beginYawPitchTransition()
        }
      } else {
        this.pendingRotatePivot = null
        this.beginPanGesture(event, camera)
        if (shouldUseAsyncPicking(this.services)) {
          this.schedulePointerPivotPick(event, camera, 'click')
        }
      }

      if (typeof element.setPointerCapture === 'function') {
        element.setPointerCapture(event.pointerId)
      }

      event.preventDefault()
    }

    this.boundPointerMove = (event: PointerEvent) => {
      this.updateCurrentMouseScreen(event)

      if (!this.enabled) {
        return
      }

      if (event.pointerType === 'touch') {
        const touchPosition = this.touchPointers.get(event.pointerId)
        if (!touchPosition) {
          return
        }

        touchPosition.set(event.clientX, event.clientY)
        if (this.pointerState === 'touch-dolly-pan' && this.touchPointers.size === 2) {
          this.updateTouchDollyPan()
          event.preventDefault()
          return
        }
      }

      if (this.pointerState === 'none') {
        return
      }

      if (this.activePointerId !== event.pointerId) {
        return
      }

      const dx = event.clientX - this.lastPointer.x
      const dy = event.clientY - this.lastPointer.y
      const dragDelta = dx * dx + dy * dy
      let didMutateView = false

      if (dragDelta === 0) {
        return
      }

      if (!this.pointerDragStarted) {
        this.pointerDragStarted = true
        if (this.pointerState === 'rotate' && !this.rotationLocked) {
          const camera = this.syncManagedCameraFromRig()
          camera.updateMatrixWorld(true)
          this.beginRotateGesture(camera)
          this.updatePivotRings(camera)
        }
      }

      if (this.pointerState === 'rotate') {
        if (this.rotationLocked) {
          // Rotation-locked mode keeps ordinary-mouse rotate gestures inert.
        } else if (event.shiftKey && this.mouseRotateMode === 'arcball') {
          const width = Math.max(this.domElement?.clientWidth ?? 1, 1)
          const angle = -(2 * Math.PI * dx * this.rotateSpeed) / width
          const forward = CAMERA_LOCAL_FORWARD.clone().applyQuaternion(this.rotation).normalize()
          SCRATCH_Q.setFromAxisAngle(forward, angle)
          this.rotation.premultiply(SCRATCH_Q)
          this.startQuat.copy(this.rotation)
          this.refreshAxesFromQuat()
          this.startArcballDir.copy(this.mouseToArcballDir(event.clientX, event.clientY))
          didMutateView = true
        } else {
          if (this.mouseRotateMode === 'yaw-pitch') {
            if (this.rotateModeTransitionEndTime <= 0) {
              this.rotateWithYawPitch(new THREE.Vector2(dx, dy))
              didMutateView = true
            }
          } else {
            this.rotateWithArcball(event.clientX, event.clientY)
            didMutateView = true
          }
        }
      } else if (this.pointerState === 'pan') {
        const camera = this.object
        didMutateView = this.panByScreenDelta(camera, dx, dy)
      }

      if (didMutateView) {
        this.syncInteractiveViewState()
      }

      this.lastPointer.set(event.clientX, event.clientY)
      event.preventDefault()
    }

    this.boundPointerUp = (event: PointerEvent) => {
      this.updateCurrentMouseScreen(event)

      if (event.pointerType === 'touch') {
        if (!this.touchPointers.has(event.pointerId)) {
          return
        }

        if (event.type === 'pointercancel') {
          this.releaseTouchPointerCaptures(element)
          this.resetTouchGesture()
          event.preventDefault()
          return
        }

        this.touchPointers.delete(event.pointerId)
        if (this.pointerState === 'touch-dolly-pan') {
          if (typeof element.releasePointerCapture === 'function') {
            element.releasePointerCapture(event.pointerId)
          }
          this.rebaseRemainingTouchPointer()
          event.preventDefault()
          return
        }
      }

      if (this.activePointerId !== event.pointerId) {
        return
      }

      this.pointerState = 'none'
      this.activePointerId = null
      this.pointerDragStarted = false
      this.panGestureAnchor = null
      if (this.pendingRotatePivot && this.mouseTargetMode === 'captured-point') {
        const camera = this.syncManagedCameraFromRig()
        this.lastCapturedPoint = this.pendingRotatePivot.clone()
        this.pivot.copy(this.pendingRotatePivot)
        this.syncOffsetFromPosition(camera)
        this.syncNavTargetFromCamera(camera)
        this.sync3DMouseCommandState()
      }
      this.pendingRotatePivot = null

      if (typeof element.releasePointerCapture === 'function') {
        element.releasePointerCapture(event.pointerId)
      }

      event.preventDefault()
    }

    this.boundWheel = (event: WheelEvent) => {
      if (!this.enabled) {
        return
      }

      this.updateCurrentMouseScreen(event)
      const camera = this.syncManagedCameraFromRig()
      if (camera instanceof THREE.OrthographicCamera) {
        this.clearWheelZoomState()
        if (!this.applyOrthographicWheelZoom(event, camera)) {
          return
        }

        this.updatePivotRings(camera)
        this.commitCameraToRig(camera)
        this.syncDeviceCameraFromRig()
        event.preventDefault()
        event.stopImmediatePropagation()
        return
      }

      if (!(camera instanceof THREE.PerspectiveCamera)) {
        return
      }

      const wheelStep = resolveWheelInputDelta(event.deltaY, event.deltaMode)
      if (wheelStep === 0) {
        return
      }

      const pointerPivot = this.getWheelPivotAtPointer(event, camera)
      this.wheelPivot = pointerPivot?.clone() ?? null
      const maxWheelDelta = Math.abs(this.zoomSpeed)
      this.wheelDelta = accumulateWheelInputDelta(
        this.wheelDelta,
        wheelStep * this.zoomSpeed,
        maxWheelDelta
      )
      event.preventDefault()
      event.stopImmediatePropagation()
    }

    this.boundDoubleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || !this.enabled) {
        return
      }

      const camera = this.syncManagedCameraFromRig()
      if (shouldUseAsyncPicking(this.services)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.scheduleDoubleClickPick(event, camera)
        return
      }

      const hit = this.pickSurfaceHitAtPointer(event, camera, 'click')
      if (!hit) {
        return
      }

      if (camera instanceof THREE.OrthographicCamera) {
        this.clearWheelZoomState()
        if (!this.startOrthographicDoubleClickZoom(event, camera, hit)) {
          return
        }

        if (!this.updateOrthographicZoomAnimation(camera, DEFAULT_FRAME_DELTA)) {
          return
        }

        this.updatePivotRings(camera)
        this.commitCameraToRig(camera)
        this.syncDeviceCameraFromRig()
        event.preventDefault()
        event.stopImmediatePropagation()
        return
      }

      if (!(camera instanceof THREE.PerspectiveCamera)) {
        return
      }

      const pivot = hit.point
      const current = this.getCurrentViewState()
      const navigationScale = this.resolveWheelNavigationScale(current.distance)
      const wheelDelta = resolveDoubleClickWheelDelta({
        position: current.position,
        pickedPoint: pivot,
        hit,
        proportionalStepRatio: LEGACY_WHEEL_STEP_RATIO,
        navigationScale,
        minStepRatio: WHEEL_MIN_STEP_RATIO,
        absoluteMinStep: this.minZoomStep,
      })
      if (wheelDelta <= 0) {
        return
      }

      this.wheelPivot = pivot.clone()
      this.wheelDelta += wheelDelta
      event.preventDefault()
      event.stopImmediatePropagation()
    }

    this.boundContextMenu = (event: MouseEvent) => {
      if (!this.enabled) {
        return
      }
      event.preventDefault()
    }

    this.boundKeyDown = (event: KeyboardEvent) => {
      if (
        !this.enabled ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        !KEYBOARD_TRANSLATION_CODES.has(event.code)
      ) {
        return
      }

      this.keyState.add(event.code)
      event.preventDefault()
    }

    this.boundKeyUp = (event: KeyboardEvent) => {
      if (!KEYBOARD_TRANSLATION_CODES.has(event.code)) {
        return
      }

      this.keyState.delete(event.code)
      event.preventDefault()
    }

    this.boundBlur = () => {
      this.keyState.clear()
      this.keyboardCurrentSpeed = 0
    }

    this.boundDocumentPointerDown = (event: PointerEvent) => {
      if (this.shouldRestoreFocusFromEventTarget(event.target)) {
        this.scheduleInteractionFocusRestore()
      }
    }

    this.boundDocumentFocusIn = (event: FocusEvent) => {
      if (this.shouldRestoreFocusFromEventTarget(event.target)) {
        this.scheduleInteractionFocusRestore()
      }
    }

    element.addEventListener('pointerdown', this.boundPointerDown)
    element.addEventListener('pointermove', this.boundPointerMove)
    element.addEventListener('pointerup', this.boundPointerUp)
    element.addEventListener('pointercancel', this.boundPointerUp)
    element.addEventListener('wheel', this.boundWheel, { passive: false })
    element.addEventListener('dblclick', this.boundDoubleClick)
    element.addEventListener('contextmenu', this.boundContextMenu)
    element.addEventListener('keydown', this.boundKeyDown)
    element.addEventListener('keyup', this.boundKeyUp)
    element.addEventListener('blur', this.boundBlur)
    element.ownerDocument.addEventListener('pointerdown', this.boundDocumentPointerDown, true)
    element.ownerDocument.addEventListener('focusin', this.boundDocumentFocusIn, true)
  }

  private removeMouseHandlers(): void {
    this.clearScheduledFocusRestore()

    if (!this.domElement) {
      this.boundPointerDown = null
      this.boundPointerMove = null
      this.boundPointerUp = null
      this.boundWheel = null
      this.boundDoubleClick = null
      this.boundContextMenu = null
      this.boundKeyDown = null
      this.boundKeyUp = null
      this.boundBlur = null
      this.boundDocumentPointerDown = null
      this.boundDocumentFocusIn = null
      this.pointerDragStarted = false
      this.panGestureAnchor = null
      this.pendingRotatePivot = null
      this.resetTouchGesture()
      this.keyState.clear()
      this.keyboardCurrentSpeed = 0
      return
    }

    if (this.boundPointerDown) {
      this.domElement.removeEventListener('pointerdown', this.boundPointerDown)
      this.boundPointerDown = null
    }
    if (this.boundPointerMove) {
      this.domElement.removeEventListener('pointermove', this.boundPointerMove)
      this.boundPointerMove = null
    }
    if (this.boundPointerUp) {
      this.domElement.removeEventListener('pointerup', this.boundPointerUp)
      this.domElement.removeEventListener('pointercancel', this.boundPointerUp)
      this.boundPointerUp = null
    }
    if (this.boundWheel) {
      this.domElement.removeEventListener('wheel', this.boundWheel)
      this.boundWheel = null
    }
    if (this.boundDoubleClick) {
      this.domElement.removeEventListener('dblclick', this.boundDoubleClick)
      this.boundDoubleClick = null
    }
    if (this.boundContextMenu) {
      this.domElement.removeEventListener('contextmenu', this.boundContextMenu)
      this.boundContextMenu = null
    }
    if (this.boundKeyDown) {
      this.domElement.removeEventListener('keydown', this.boundKeyDown)
      this.boundKeyDown = null
    }
    if (this.boundKeyUp) {
      this.domElement.removeEventListener('keyup', this.boundKeyUp)
      this.boundKeyUp = null
    }
    if (this.boundBlur) {
      this.domElement.removeEventListener('blur', this.boundBlur)
      this.boundBlur = null
    }
    if (this.boundDocumentPointerDown) {
      this.domElement.ownerDocument.removeEventListener(
        'pointerdown',
        this.boundDocumentPointerDown,
        true
      )
      this.boundDocumentPointerDown = null
    }
    if (this.boundDocumentFocusIn) {
      this.domElement.ownerDocument.removeEventListener('focusin', this.boundDocumentFocusIn, true)
      this.boundDocumentFocusIn = null
    }

    this.clearWheelZoomState()
    this.releaseTouchPointerCaptures(this.domElement)
    this.resetTouchGesture()
    this.pointerDragStarted = false
    this.panGestureAnchor = null
    this.pendingRotatePivot = null
    this.keyState.clear()
    this.keyboardCurrentSpeed = 0
  }

  private updateLegacyWheelZoom(): void {
    const safeDelta = DEFAULT_FRAME_DELTA
    const fade = 0.5 ** (LEGACY_WHEEL_FADE_FACTOR * safeDelta)
    const progression = 1 - fade
    const wheelPivotPending = this.wheelPivotPending
    if (this.wheelDelta !== 0 && !wheelPivotPending) {
      const current = this.getCurrentViewState()
      const navigationScale = this.resolveWheelNavigationScale(current.distance)
      if (this.wheelPivot) {
        const step = resolvePickedPointWheelStep({
          position: current.position,
          target: current.target,
          forward: current.forward,
          pickedPoint: this.wheelPivot,
          currentZoomDelta: this.zoomDelta,
          wheelDelta: this.wheelDelta,
          proportionalStepRatio: LEGACY_WHEEL_STEP_RATIO,
          navigationScale,
          minStepRatio: WHEEL_MIN_STEP_RATIO,
          absoluteMinStep: this.minZoomStep,
        })

        if (step.crossedPickedPoint) {
          this.zoomDelta.set(0, 0, 0)
          this.zoomDistance = null
          this.applyWheelCameraState(step.nextPosition, step.nextTarget, {
            preserveRotationPivot:
              this.mouseTargetMode === 'captured-point' && this.lastCapturedPoint !== null,
          })
        } else {
          this.zoomDelta.copy(step.nextZoomDelta)
          this.zoomDistance = step.nextZoomDistance
        }
      } else {
        const moveDistance = resolveWheelForwardStep({
          cameraTargetDistance: current.distance,
          wheelDelta: this.wheelDelta,
          proportionalStepRatio: FREE_WHEEL_STEP_RATIO,
          navigationScale,
          minStepRatio: WHEEL_MIN_STEP_RATIO,
          absoluteMinStep: this.minZoomStep,
        })
        const moveVector = current.forward.multiplyScalar(moveDistance)
        if (moveVector.lengthSq() > 1e-12) {
          this.zoomDelta.add(moveVector)
        }
        this.zoomDistance = null
      }
    }

    if (this.zoomDelta.lengthSq() > 1e-12) {
      const current = this.getCurrentViewState()
      const movement = this.zoomDelta.clone().multiplyScalar(progression)
      if (movement.lengthSq() > 1e-12) {
        const nextPosition = current.position.clone().add(movement)
        const nextTarget =
          this.zoomDistance == null
            ? current.target.clone().add(movement)
            : nextPosition.clone().addScaledVector(current.forward, this.zoomDistance)
        this.applyWheelCameraState(nextPosition, nextTarget)
      }
    }

    this.zoomDelta.multiplyScalar(fade)
    if (this.zoomDelta.lengthSq() <= 1e-12) {
      this.zoomDelta.set(0, 0, 0)
      this.zoomDistance = null
    }
    if (!wheelPivotPending) {
      this.wheelDelta = 0
      this.wheelPivot = null
    }
  }

  private clearWheelZoomState(): void {
    this.wheelDelta = 0
    this.zoomDelta.set(0, 0, 0)
    this.wheelPivot = null
    this.wheelPivotPending = false
    this.zoomDistance = null
    this.clearOrthographicZoomAnimation()
    this.resetWheelPickCache()
  }

  private resetKeyboardMotionProfile(distance: number): void {
    this.keyboardMaxSpeed = this.resolveKeyboardMaxSpeed(distance)
    this.keyboardCurrentSpeed = 0
  }

  private advanceKeyboardSpeed(deltaTime: number): number {
    const acceleration = this.keyboardMaxSpeed / KEYBOARD_ACCELERATION_DURATION
    this.keyboardCurrentSpeed = Math.min(
      this.keyboardMaxSpeed,
      this.keyboardCurrentSpeed + acceleration * deltaTime
    )
    return this.keyboardCurrentSpeed * deltaTime
  }

  private resolveKeyboardMaxSpeed(distance: number): number {
    return this.resolveKeyboardNavigationScale(distance)
  }

  private resolveWheelNavigationScale(
    distance: number,
    absoluteMinimum = NAVIGATION_SCALE_ABSOLUTE_MINIMUM
  ): number {
    return resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum,
    })
  }

  private resolvePointerPanNavigationScale(
    distance: number,
    absoluteMinimum = NAVIGATION_SCALE_ABSOLUTE_MINIMUM
  ): number {
    return resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum,
    })
  }

  private resolveKeyboardNavigationScale(distance: number): number {
    return resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum: MIN_KEYBOARD_MAX_SPEED,
    })
  }

  private updateKeyboardTranslation(deltaTime?: number): void {
    if (this.keyState.size === 0) {
      this.keyboardCurrentSpeed = 0
      return
    }

    const current = this.getCurrentViewState()
    if (current.forward.lengthSq() <= 1e-12) {
      this.keyboardCurrentSpeed = 0
      return
    }

    const safeDelta =
      deltaTime && Number.isFinite(deltaTime) && deltaTime > 0 ? deltaTime : DEFAULT_FRAME_DELTA
    const yaw = this.getStableYaw(current.forward)
    const planarForward = new THREE.Vector3(0, 1, 0).applyAxisAngle(WORLD_UP, yaw).normalize()
    const side = new THREE.Vector3(1, 0, 0).applyAxisAngle(WORLD_UP, yaw).normalize()
    const movement = new THREE.Vector3()

    const moveForward = this.keyState.has('KeyW') || this.keyState.has('ArrowUp')
    const moveBackward = this.keyState.has('KeyS') || this.keyState.has('ArrowDown')
    const moveLeft = this.keyState.has('KeyA') || this.keyState.has('ArrowLeft')
    const moveRight = this.keyState.has('KeyD') || this.keyState.has('ArrowRight')
    const moveUp = this.keyState.has('KeyQ')
    const moveDown = this.keyState.has('KeyE')

    if (moveForward !== moveBackward) {
      movement.addScaledVector(planarForward, moveForward ? 1 : -1)
    }

    if (moveLeft !== moveRight) {
      movement.addScaledVector(side, moveRight ? 1 : -1)
    }

    if (moveUp !== moveDown) {
      movement.addScaledVector(WORLD_UP, moveUp ? 1 : -1)
    }

    if (movement.lengthSq() <= 1e-12) {
      this.keyboardCurrentSpeed = 0
      return
    }

    const frameSpeed = this.advanceKeyboardSpeed(safeDelta) * this.panSpeed
    movement.multiplyScalar(frameSpeed)

    this.translateView(movement)
  }

  private shouldRestoreFocusFromEventTarget(target: EventTarget | null): boolean {
    if (!this.enabled || !this.interactionElement) {
      return false
    }

    if (!(target instanceof Node)) {
      return false
    }

    if (this.interactionElement.contains(target)) {
      return false
    }

    if (isEditableElement(target)) {
      return false
    }

    return true
  }

  private scheduleInteractionFocusRestore(): void {
    this.clearScheduledFocusRestore()
    this.focusRestoreTimer = window.setTimeout(() => {
      this.focusRestoreTimer = 0
      this.restoreInteractionFocus()
    }, 0)
  }

  private clearScheduledFocusRestore(): void {
    if (this.focusRestoreTimer !== 0) {
      window.clearTimeout(this.focusRestoreTimer)
      this.focusRestoreTimer = 0
    }
    if (this.focusRestoreFrame !== 0) {
      window.cancelAnimationFrame(this.focusRestoreFrame)
      this.focusRestoreFrame = 0
    }
  }

  private restoreInteractionFocus(): void {
    if (!this.enabled || !this.interactionElement) {
      return
    }

    this.interactionElement.focus()

    if (this.nav && this.navCreated) {
      this.nav.update3dcontroller({ focus: true })
    }
  }

  private requestFocusRecovery(): void {
    this.scheduleInteractionFocusRestore()
    this.focusRestoreFrame = window.requestAnimationFrame(() => {
      this.focusRestoreFrame = 0
      this.restoreInteractionFocus()
    })
  }

  private pivotToScreen(): THREE.Vector2 {
    if (!this.domElement) {
      return new THREE.Vector2(0, 0)
    }

    const rect = this.domElement.getBoundingClientRect()
    const projected = this.pivot.clone().project(this.object)
    return new THREE.Vector2(
      (projected.x + 1) * 0.5 * rect.width,
      (1 - projected.y) * 0.5 * rect.height
    )
  }

  private mouseToArcballDir(clientX: number, clientY: number): THREE.Vector3 {
    const rect = this.domElement?.getBoundingClientRect()
    if (!rect) {
      return this.lastForward.clone()
    }

    const radius = Math.min(rect.width, rect.height) * 0.5
    let centerX = this.arcballCenterScreen.x
    let centerY = this.arcballCenterScreen.y
    centerX = Math.max(radius, Math.min(rect.width - radius, centerX))
    centerY = Math.max(radius, Math.min(rect.height - radius, centerY))

    const x = clientX - rect.left - centerX
    const y = centerY - (clientY - rect.top)
    const vx = THREE.MathUtils.clamp((x * this.rotateSpeed) / radius, -1, 1)
    const vy = THREE.MathUtils.clamp((y * this.rotateSpeed) / radius, -1, 1)
    const d2 = vx * vx + vy * vy
    const vz = d2 >= 1 ? 0 : Math.sqrt(1 - d2)

    this.arcballDir
      .set(0, 0, 0)
      .addScaledVector(this.lastRight, vx)
      .addScaledVector(this.lastUp, vy)
      .addScaledVector(this.lastForward, -vz)

    return this.arcballDir.lengthSq() > 1e-8
      ? this.arcballDir.normalize()
      : this.lastForward.clone()
  }

  private updateCurrentMouseScreen(event: MouseEvent | PointerEvent | WheelEvent): void {
    if (!this.domElement) {
      return
    }

    const rect = this.domElement.getBoundingClientRect()
    this.currentMouseScreen.set(event.clientX - rect.left, event.clientY - rect.top)
  }

  private getWheelPivotAtPointer(event: WheelEvent, camera: THREE.Camera): THREE.Vector3 | null {
    const reusablePivot = this.getReusableWheelPivot()
    if (reusablePivot !== undefined) {
      return reusablePivot
    }

    // Do not abort an in-flight GPU pivot pick on every wheel event.  The coordinator already
    // coalesces move requests; keeping this request alive avoids wasting the submitted compute
    // and readback work while the current wheel delta continues to accumulate.
    if (this.wheelPivotPending) {
      return null
    }

    if (shouldUseAsyncPicking(this.services)) {
      this.scheduleWheelPivotPick(event, camera)
      return null
    }

    const pivot = this.pickPivotAtPointer(event, camera)
    this.cacheWheelPivotAtCurrentMouse(pivot)
    return pivot
  }

  private getReusableWheelPivot(): THREE.Vector3 | null | undefined {
    if (
      !this.lastWheelPickScreen ||
      performance.now() - this.lastWheelPickAt >
        (this.lastWheelPickedPivot ? WHEEL_GESTURE_IDLE_MS : WHEEL_PICK_REUSE_MS)
    ) {
      return undefined
    }

    const deltaX = this.currentMouseScreen.x - this.lastWheelPickScreen.x
    const deltaY = this.currentMouseScreen.y - this.lastWheelPickScreen.y
    if (deltaX * deltaX + deltaY * deltaY > WHEEL_PICK_REUSE_DISTANCE_PX ** 2) {
      return undefined
    }

    // Extend the reuse window on every wheel event.  Trackpads and high-resolution wheels can
    // emit events more than the reuse window apart while still belonging to one gesture; without refreshing
    // this timestamp, the next event starts another full GPU pick (cull + projector + radix +
    // readback) even though the pointer anchor has not changed.
    if (this.lastWheelPickedPivot) {
      const camera = this.syncManagedCameraFromRig()
      if (
        !isPointBeyondNativeControlPickPlane(
          camera,
          this.lastWheelPickedPivot,
          this.surfacePickNearOffset
        )
      ) {
        this.resetWheelPickCache()
        return undefined
      }
      this.lastWheelPickAt = performance.now()
    }
    return this.lastWheelPickedPivot?.clone() ?? null
  }

  private cacheWheelPivotAtCurrentMouse(pivot: THREE.Vector3 | null): void {
    if (!this.lastWheelPickScreen) {
      this.lastWheelPickScreen = new THREE.Vector2()
    }

    this.lastWheelPickScreen.copy(this.currentMouseScreen)
    this.lastWheelPickAt = performance.now()
    this.lastWheelPickedPivot = pivot?.clone() ?? null
  }

  private resetWheelPickCache(): void {
    this.lastWheelPickAt = -Infinity
    this.lastWheelPickScreen = null
    this.lastWheelPickedPivot = null
  }

  private scheduleWheelPivotPick(event: WheelEvent, camera: THREE.Camera): void {
    if (this.wheelPivotPending) return
    const version = ++this.pickRequestVersion
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    this.wheelPivotPending = true
    void this.pickSurfaceHitAtPointerAsync(event, camera, 'move', controller.signal)
      .then((hit) => {
        if (version !== this.pickRequestVersion || !this.wheelPivotPending || !this.enabled) {
          return
        }
        if (!isCurrentViewportCamera(this.services!, camera)) {
          this.wheelPivotPending = false
          this.wheelPivot = null
          return
        }
        const pivot = hit?.point.clone() ?? null
        this.cacheWheelPivotAtCurrentMouse(pivot)
        this.wheelPivotPending = false
        this.wheelPivot = pivot
      })
      .catch(() => {
        if (version !== this.pickRequestVersion || !this.wheelPivotPending || !this.enabled) {
          return
        }
        if (!isCurrentViewportCamera(this.services!, camera)) {
          this.wheelPivotPending = false
          this.wheelPivot = null
          return
        }
        this.wheelPivotPending = false
        this.wheelPivot = null
      })
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private pickPivotAtPointer(
    event: MouseEvent | PointerEvent | WheelEvent,
    camera: THREE.Camera,
    interactionType: 'click' | 'move' = 'move'
  ): THREE.Vector3 | null {
    return this.pickSurfaceHitAtPointer(event, camera, interactionType)?.point.clone() ?? null
  }

  private pickSurfaceHitAtPointer(
    event: MouseEvent | PointerEvent | WheelEvent,
    camera: THREE.Camera,
    interactionType: 'click' | 'move' = 'move'
  ): NativeControlSurfaceQueryHit | null {
    if (!this.domElement) {
      return null
    }

    const rect = this.domElement.getBoundingClientRect()
    return this.pickSurfaceHitAtScreen(
      new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top),
      camera,
      interactionType
    )
  }

  private rotateWithYawPitch(delta: THREE.Vector2): void {
    if (!this.domElement) {
      return
    }

    const width = Math.max(this.domElement.clientWidth, 1)
    const height = Math.max(this.domElement.clientHeight, 1)
    const yawDelta = -(delta.x / width) * LEGACY_ROTATION_SPEED * this.rotateSpeed * 0.5
    let pitchDelta = -(delta.y / height) * LEGACY_ROTATION_SPEED * this.rotateSpeed * 0.2

    const position = this.pivot
      .clone()
      .add(SCRATCH_V3.copy(this.offset).applyQuaternion(this.rotation))
    const forward = CAMERA_LOCAL_FORWARD.clone().applyQuaternion(this.rotation).normalize()
    const target = this.navTarget.clone()

    const yaw = this.getStableYaw(forward)
    const currentPitch = Math.atan2(
      forward.z,
      Math.sqrt(forward.x * forward.x + forward.y * forward.y)
    )
    const nextPitch = THREE.MathUtils.clamp(
      currentPitch + pitchDelta,
      -MAX_ABSOLUTE_PITCH,
      MAX_ABSOLUTE_PITCH
    )
    pitchDelta = nextPitch - currentPitch

    if (Math.abs(pitchDelta) <= 1e-8 && Math.abs(yawDelta) <= 1e-8) {
      return
    }

    const side = new THREE.Vector3(1, 0, 0).applyAxisAngle(WORLD_UP, yaw).normalize()

    const pivotToCamera = position.clone().sub(this.pivot)
    const pivotToTarget = target.clone().sub(this.pivot)
    pivotToCamera.applyAxisAngle(side, pitchDelta)
    pivotToTarget.applyAxisAngle(side, pitchDelta)

    pivotToCamera.applyAxisAngle(WORLD_UP, yawDelta)
    pivotToTarget.applyAxisAngle(WORLD_UP, yawDelta)

    const nextPosition = this.pivot.clone().add(pivotToCamera)
    const nextTarget = this.pivot.clone().add(pivotToTarget)
    const nextForward = nextTarget.clone().sub(nextPosition).normalize()
    const nextYaw = yaw + yawDelta
    const nextSide = new THREE.Vector3(1, 0, 0).applyAxisAngle(WORLD_UP, nextYaw).normalize()
    const nextUp = nextSide.clone().cross(nextForward).normalize()
    const nextRight = nextForward.clone().cross(nextUp).normalize()

    SCRATCH_M4.makeBasis(nextRight, nextUp, nextForward.clone().negate())
    this.rotation.setFromRotationMatrix(SCRATCH_M4)
    this.lastStableYaw = nextYaw
    this.navTarget.copy(nextTarget)

    SCRATCH_V3.subVectors(nextPosition, this.pivot)
    this.offset.copy(SCRATCH_V3.applyQuaternion(SCRATCH_Q.copy(this.rotation).invert()))
    this.refreshAxesFromQuat()
  }

  private rotateWithArcball(clientX: number, clientY: number): void {
    const currentDir = this.mouseToArcballDir(clientX, clientY)
    SCRATCH_Q.setFromUnitVectors(currentDir, this.startArcballDir)
    this.rotation.copy(this.startQuat).premultiply(SCRATCH_Q)
  }

  private beginYawPitchTransition(): void {
    if (!this.hasInternalState) {
      return
    }

    const currentPosition = this.pivot
      .clone()
      .add(SCRATCH_V3.copy(this.offset).applyQuaternion(this.rotation))
    const forward = CAMERA_LOCAL_FORWARD.clone().applyQuaternion(this.rotation).normalize()
    const targetRotation = this.createYawPitchAlignedRotation(forward)

    if (this.rotation.angleTo(targetRotation) <= 1e-5) {
      this.rotateModeTransitionEndTime = 0
      this.rotation.copy(targetRotation)
      this.syncOffsetFromWorldPosition(currentPosition)
      this.refreshAxesFromQuat()
      return
    }

    this.transitionWorldPosition.copy(currentPosition)
    this.transitionFromRotation.copy(this.rotation)
    this.transitionToRotation.copy(targetRotation)
    this.rotateModeTransitionEndTime = performance.now() + ROTATE_MODE_TRANSITION_DURATION_MS
  }

  private stepRotateModeTransition(): void {
    if (this.rotateModeTransitionEndTime <= 0) {
      return
    }

    const now = performance.now()
    const startTime = this.rotateModeTransitionEndTime - ROTATE_MODE_TRANSITION_DURATION_MS
    const progress = THREE.MathUtils.clamp(
      (now - startTime) / ROTATE_MODE_TRANSITION_DURATION_MS,
      0,
      1
    )
    const eased = 1 - (1 - progress) ** 3

    this.rotation.copy(this.transitionFromRotation).slerp(this.transitionToRotation, eased)
    this.syncOffsetFromWorldPosition(this.transitionWorldPosition)
    this.refreshAxesFromQuat()

    if (progress >= 1) {
      this.rotateModeTransitionEndTime = 0
    }
  }

  private getStableYaw(forward: THREE.Vector3): number {
    const horizontalLength = Math.sqrt(forward.x * forward.x + forward.y * forward.y)

    if (horizontalLength > 1e-6) {
      this.lastStableYaw = Math.atan2(forward.y, forward.x) - Math.PI / 2
    }

    return this.lastStableYaw
  }

  private createYawPitchAlignedRotation(forward: THREE.Vector3): THREE.Quaternion {
    const yaw = this.getStableYaw(forward)
    const side = new THREE.Vector3(1, 0, 0).applyAxisAngle(WORLD_UP, yaw).normalize()
    const up = side.clone().cross(forward).normalize()
    const right = forward.clone().cross(up).normalize()

    SCRATCH_M4.makeBasis(right, up, forward.clone().negate())
    return new THREE.Quaternion().setFromRotationMatrix(SCRATCH_M4)
  }

  private updateViewportMetrics(): void {
    const { width, height } = this.services?.getViewportSize() ?? { width: 1, height: 1 }
    const viewportWidth = Math.max(width, 1)
    const viewportHeight = Math.max(height, 1)
    const camera = this.deviceCamera

    this.glState.viewportWidth = viewportWidth
    this.glState.viewportHeight = viewportHeight
    this.glState.frustumNear = camera.near
    this.glState.frustumFar = camera.far

    if (camera instanceof THREE.PerspectiveCamera) {
      const aspect = viewportWidth / viewportHeight
      this.glState.fov = camera.fov
      const left = -8 * Math.tan((camera.fov * Math.PI) / 360) * aspect
      this.glState.left = left
      this.glState.right = -left
      this.glState.bottom = left / aspect
      this.glState.top = -this.glState.bottom
      return
    }

    const safeZoom = Math.max(camera.zoom, CAMERA_EPSILON)
    this.glState.left = camera.left / safeZoom
    this.glState.right = camera.right / safeZoom
    this.glState.bottom = camera.bottom / safeZoom
    this.glState.top = camera.top / safeZoom
  }

  private getDeviceCameraForMode(
    mode: CameraMode
  ): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    this.deviceCamera =
      mode === CameraMode.Orthographic
        ? this.orthographicDeviceCamera
        : this.perspectiveDeviceCamera
    return this.deviceCamera
  }

  private getOrthographicReferenceHalfHeight(distance: number): number {
    const fov = this.services?.rig.getState().fov ?? this.glState.fov
    return Math.max(distance * Math.tan((fov * Math.PI) / 360), CAMERA_EPSILON)
  }

  private syncOrthographicDeviceProjection(
    camera: THREE.OrthographicCamera,
    distance: number
  ): void {
    const aspect =
      Math.max(this.glState.viewportWidth, 1) / Math.max(this.glState.viewportHeight, 1)
    const halfHeight = this.getOrthographicReferenceHalfHeight(distance)
    const halfWidth = halfHeight * aspect
    const viewWidth = Math.max(this.glState.right - this.glState.left, CAMERA_EPSILON)
    const viewHeight = Math.max(this.glState.top - this.glState.bottom, CAMERA_EPSILON)
    const zoomX = (2 * halfWidth) / viewWidth
    const zoomY = (2 * halfHeight) / viewHeight

    camera.left = -halfWidth
    camera.right = halfWidth
    camera.bottom = -halfHeight
    camera.top = halfHeight
    camera.zoom = Math.max(Math.min(zoomX, zoomY), CAMERA_EPSILON)
  }

  private getActiveRotateCommandId(): string {
    return this.mouseRotateMode === 'yaw-pitch'
      ? SPACEMOUSE_ROTATE_YAW_PITCH_COMMAND_ID
      : SPACEMOUSE_ROTATE_ARCBALL_COMMAND_ID
  }

  private create3DMouseTargetCategory(): SpaceMouseCommandCategory {
    return {
      id: SPACEMOUSE_TARGET_CATEGORY_ID,
      label: 'Target Mode',
      type: SPACEMOUSE_CATEGORY_NODE,
      nodes: [
        {
          id: SPACEMOUSE_TARGET_BOUNDS_CENTER_COMMAND_ID,
          label: 'Bounds Center',
          type: SPACEMOUSE_ACTION_NODE,
          description: 'Rotate around the scene bounds center',
        },
        {
          id: SPACEMOUSE_TARGET_CAPTURED_POINT_COMMAND_ID,
          label: 'Captured Point',
          type: SPACEMOUSE_ACTION_NODE,
          description: 'Rotate around the latest ordinary mouse capture point',
        },
      ],
    }
  }

  private create3DMouseHistoryCategory(): SpaceMouseCommandCategory {
    return {
      id: SPACEMOUSE_HISTORY_CATEGORY_ID,
      label: 'History',
      type: SPACEMOUSE_CATEGORY_NODE,
      nodes: [
        {
          id: SPACEMOUSE_UNDO_COMMAND_ID,
          label: 'Undo',
          type: SPACEMOUSE_ACTION_NODE,
          description: 'Run the active application undo command',
        },
        {
          id: SPACEMOUSE_REDO_COMMAND_ID,
          label: 'Redo',
          type: SPACEMOUSE_ACTION_NODE,
          description: 'Run the active application redo command',
        },
      ],
    }
  }

  private create3DMouseCommandTree(): SpaceMouseCommandTree {
    return {
      nodes: [
        {
          id: SPACEMOUSE_ROTATE_ACTION_SET_ID,
          label: 'Mouse Navigation',
          type: SPACEMOUSE_ACTIONSET_NODE,
          nodes: [
            {
              id: SPACEMOUSE_ROTATE_CATEGORY_ID,
              label: 'Rotate Mode',
              type: SPACEMOUSE_CATEGORY_NODE,
              nodes: [
                {
                  id: SPACEMOUSE_ROTATE_ARCBALL_COMMAND_ID,
                  label: 'Arcball',
                  type: SPACEMOUSE_ACTION_NODE,
                  description: 'Switch to arcball rotation',
                },
                {
                  id: SPACEMOUSE_ROTATE_YAW_PITCH_COMMAND_ID,
                  label: 'Yaw/Pitch',
                  type: SPACEMOUSE_ACTION_NODE,
                  description: 'Switch to yaw/pitch rotation',
                },
              ],
            },
            this.create3DMouseTargetCategory(),
            this.create3DMouseHistoryCategory(),
          ],
        },
      ],
    }
  }

  private push3DMouseControllerUpdate(payload: Record<string, unknown>): void {
    if (!this.nav || !this.navCreated) {
      return
    }

    try {
      const result = this.nav.update3dcontroller(payload) as unknown
      if (
        typeof result === 'object' &&
        result !== null &&
        'catch' in result &&
        typeof result.catch === 'function'
      ) {
        void result.catch(() => {})
      }
    } catch {}
  }

  private sync3DMouseCommandState(includeCommandTree = false): void {
    const commands: Record<string, unknown> = {
      activeCommand: this.getActiveRotateCommandId(),
    }

    if (includeCommandTree) {
      commands.tree = this.create3DMouseCommandTree()
    }

    const payload: Record<string, unknown> = {
      commands,
      settings: {
        ActionSet: SPACEMOUSE_ROTATE_ACTION_SET_ID,
      },
    }

    if (includeCommandTree) {
      payload.frame = { timingSource: 1 }
    }

    this.push3DMouseControllerUpdate(payload)
  }

  private normalize3DMouseCommandId(command: unknown): string | null {
    if (typeof command === 'string' || typeof command === 'number') {
      return `${command}`
    }

    if (Array.isArray(command)) {
      for (const candidate of command) {
        const commandId = this.normalize3DMouseCommandId(candidate)
        if (commandId) {
          return commandId
        }
      }

      return null
    }

    if (!command || typeof command !== 'object') {
      return null
    }

    const record = command as Record<string, unknown>
    for (const key of ['id', 'ID', 'command', 'name', 'Name']) {
      const value = record[key]
      if (typeof value === 'string' || typeof value === 'number') {
        return `${value}`
      }
    }

    return null
  }

  private handle3DMouseActiveCommand(command: unknown): void {
    if (!this.deviceNavigationEnabled) {
      return
    }

    const commandId = this.normalize3DMouseCommandId(command)
    if (!commandId) {
      return
    }

    if (commandId === SPACEMOUSE_UNDO_COMMAND_ID) {
      this.appCommandHandlers.undo?.()
      this.requestFocusRecovery()
      return
    }

    if (commandId === SPACEMOUSE_REDO_COMMAND_ID) {
      this.appCommandHandlers.redo?.()
      this.requestFocusRecovery()
      return
    }

    this.promoteNavigationProfileForConfirmed3DMouseInput()

    if (commandId === SPACEMOUSE_ROTATE_ARCBALL_COMMAND_ID) {
      this.setMouseRotateMode('arcball')
      return
    }

    if (commandId === SPACEMOUSE_ROTATE_YAW_PITCH_COMMAND_ID) {
      this.setMouseRotateMode('yaw-pitch')
      return
    }

    if (commandId === SPACEMOUSE_TARGET_BOUNDS_CENTER_COMMAND_ID) {
      this.setMouseTargetMode('bounds-center')
      return
    }

    if (commandId === SPACEMOUSE_TARGET_CAPTURED_POINT_COMMAND_ID) {
      this.setMouseTargetMode('captured-point')
    }
  }

  private connect3DMouse(container: HTMLElement): void {
    this.disconnect3DMouse()

    try {
      const client = this.build3DConnexionClient(container)
      this.nav = new _3Dconnexion(client)
      this.nav.connect()
      container.focus()
    } catch (error) {
      this.nav = null
      log.warn(
        '[SpaceMouseController] failed to initialize 3Dconnexion client: ' +
          (error instanceof Error ? error.message : String(error))
      )
    }
  }

  private disconnect3DMouse(): void {
    if (!this.nav) {
      return
    }

    try {
      if (this.navCreated) {
        this.nav.delete3dmouse()
      }
      this.nav.close()
    } finally {
      this.nav = null
      this.navCreated = false
      this.navIsActive = false
      this.promoteHybridProfileOnNextConfirmed3DMouseInput = false
    }
  }

  private build3DConnexionClient(container: HTMLElement): I3DConnexionClient {
    return {
      onConnect: () => {
        this.nav?.create3dmouse(container, this.appName)
      },
      onDisconnect: () => {
        this.navCreated = false
        this.navIsActive = false
      },
      on3dmouseCreated: () => {
        this.navCreated = true
        this.sync3DMouseCommandState(true)
      },
      onStartMotion: () => {
        if (!this.deviceNavigationEnabled) {
          this.navIsActive = false
          return
        }
        this.promoteNavigationProfileForConfirmed3DMouseInput()
        this.navIsActive = true
      },
      onStopMotion: () => {
        this.navIsActive = false
      },
      getCoordinateSystem: () => [...IDENTITY_16],
      getConstructionPlane: () => this.getPlane(0, 0, 0),
      getFloorPlane: () => this.getPlane(0, 0, 0),
      getUnitsToMeters: () => this.unitsToMeters,
      getFov: () => {
        const camera = this.deviceCamera
        const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : this.glState.fov
        const aspect =
          camera instanceof THREE.PerspectiveCamera
            ? camera.aspect
            : Math.max(this.glState.viewportWidth, 1) / Math.max(this.glState.viewportHeight, 1)
        return 2 * Math.atan(Math.tan((fov * Math.PI) / 360) * Math.sqrt(1 + aspect * aspect))
      },
      getFrontView: () => [...SPACEMOUSE_FRONT_VIEW],
      getLookAt: () => this.getLookAt(),
      getModelExtents: () => this.getModelExtents(),
      getPerspective: () => this.deviceCamera instanceof THREE.PerspectiveCamera,
      getPivotPosition: () => this.getSpaceMousePivot().toArray(),
      getPointerPosition: () => this.getPointerPosition(),
      getViewRotatable: () => this.deviceNavigationEnabled && !this.rotationLocked,
      getViewExtents: () => {
        const camera = this.deviceCamera
        if (camera instanceof THREE.OrthographicCamera) {
          const safeZoom = Math.max(camera.zoom, CAMERA_EPSILON)
          return [
            camera.left / safeZoom,
            camera.bottom / safeZoom,
            -camera.far,
            camera.right / safeZoom,
            camera.top / safeZoom,
            -camera.near,
          ]
        }

        return [
          this.glState.left,
          this.glState.bottom,
          -camera.far,
          this.glState.right,
          this.glState.top,
          -camera.near,
        ]
      },
      getViewFrustum: () => {
        const camera = this.deviceCamera
        if (camera instanceof THREE.OrthographicCamera) {
          const safeZoom = Math.max(camera.zoom, CAMERA_EPSILON)
          return [
            camera.left / safeZoom,
            camera.right / safeZoom,
            camera.bottom / safeZoom,
            camera.top / safeZoom,
            camera.near,
            camera.far,
          ]
        }

        const tanHalfFov = Math.tan((camera.fov * Math.PI) / 360)
        const bottom = -camera.near * tanHalfFov
        const left = bottom * camera.aspect
        return [left, -left, bottom, -bottom, camera.near, camera.far]
      },
      getViewMatrix: () => this.deviceCamera.matrixWorld.toArray(),
      getViewTarget: () => this.getEffectiveTargetPivot().toArray(),
      setActiveCommand: (command: unknown) => {
        this.handle3DMouseActiveCommand(command)
      },
      setLookFrom: (data: number[]) => {
        this.lookFrom.set(data[0] ?? 0, data[1] ?? 0, data[2] ?? 0)
      },
      setLookDirection: (data: number[]) => {
        this.lookDirection.set(data[0] ?? 0, data[1] ?? 0, data[2] ?? -1)
      },
      setLookAperture: () => {},
      setSelectionOnly: (data: boolean) => {
        this.selectionOnly = data
      },
      setViewExtents: (data: number[]) => {
        if (!this.deviceNavigationEnabled) {
          return
        }

        this.glState.left = data[0] ?? this.glState.left
        this.glState.bottom = data[1] ?? this.glState.bottom
        this.glState.right = data[3] ?? this.glState.right
        this.glState.top = data[4] ?? this.glState.top
        if (this.deviceCamera instanceof THREE.OrthographicCamera) {
          const distance = Math.max(
            this.deviceCamera.position.distanceTo(this.navTarget),
            CAMERA_EPSILON
          )
          this.syncOrthographicDeviceProjection(this.deviceCamera, distance)
          this.deviceCamera.updateProjectionMatrix()
        }
      },
      setViewMatrix: (data: number[]) => {
        if (!this.deviceNavigationEnabled) {
          return
        }

        this.promoteNavigationProfileForConfirmed3DMouseInput()
        const effectivePivot = this.getEffectiveTargetPivot()
        this.pivot.copy(effectivePivot)
        this.syncNavTargetToEffectivePivot()
        const matrix = new THREE.Matrix4().fromArray(data)
        matrix.decompose(
          this.deviceCamera.position,
          this.deviceCamera.quaternion,
          this.deviceCamera.scale
        )
        if (this.rotationLocked) {
          this.deviceCamera.quaternion.copy(this.rotation)
        } else if (this.mouseRotateMode === 'yaw-pitch') {
          const forward = CAMERA_LOCAL_FORWARD.clone()
            .applyQuaternion(this.deviceCamera.quaternion)
            .normalize()
          this.deviceCamera.quaternion.copy(this.createYawPitchAlignedRotation(forward))
        }
        this.deviceCamera.updateMatrixWorld(true)
        this.syncInternalFromCamera(this.deviceCamera, false)
        this.syncNavTargetFromCamera(this.deviceCamera)
        if (this.deviceCamera instanceof THREE.OrthographicCamera) {
          const distance = Math.max(
            this.deviceCamera.position.distanceTo(this.navTarget),
            CAMERA_EPSILON
          )
          this.syncOrthographicDeviceProjection(this.deviceCamera, distance)
          this.deviceCamera.updateProjectionMatrix()
        }
        this.commitCameraToRig(this.deviceCamera)
      },
      setFov: (data: number) => {
        if (!this.deviceNavigationEnabled) {
          return
        }

        if (this.deviceCamera instanceof THREE.PerspectiveCamera) {
          this.deviceCamera.fov = (data * 180) / Math.PI
          this.deviceCamera.updateProjectionMatrix()
        } else {
          this.glState.fov = (data * 180) / Math.PI
          const distance = Math.max(
            this.deviceCamera.position.distanceTo(this.navTarget),
            CAMERA_EPSILON
          )
          this.syncOrthographicDeviceProjection(this.deviceCamera, distance)
          this.deviceCamera.updateProjectionMatrix()
        }
      },
      setTarget: (data: [number, number, number]) => {
        if (!this.deviceNavigationEnabled) {
          return
        }

        this.promoteNavigationProfileForConfirmed3DMouseInput()
        void data
        this.syncNavTargetToEffectivePivot()
      },
      setTransaction: () => {},
    }
  }

  private syncDeviceCameraFromRig(): void {
    if (!this.services) {
      return
    }

    const state = this.services.rig.getState()
    const camera = this.getDeviceCameraForMode(state.mode)
    camera.position.copy(state.position)
    camera.up.copy(state.up)
    camera.near = this.services.rig.near
    camera.far = this.services.rig.far

    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = state.fov
      camera.aspect =
        Math.max(this.glState.viewportWidth, 1) / Math.max(this.glState.viewportHeight, 1)
    } else {
      const distance = Math.max(state.position.distanceTo(state.target), CAMERA_EPSILON)
      this.glState.fov = state.fov
      this.syncOrthographicDeviceProjection(camera, distance)
      camera.zoom = Math.max(state.zoom, CAMERA_EPSILON)
    }

    camera.lookAt(state.target)
    camera.updateProjectionMatrix()
    camera.updateMatrixWorld(true)
    this.updateViewportMetrics()
  }

  private getPlane(px: number, py: number, pz: number): number[] {
    const d0 = WORLD_UP.x * px + WORLD_UP.y * py + WORLD_UP.z * pz
    return [WORLD_UP.x, WORLD_UP.y, WORLD_UP.z, -d0]
  }

  private getModelExtents(): number[] {
    const bounds = this.services?.getSceneBounds?.('fit') ?? this.services?.getSceneBounds?.()
    if (bounds && !bounds.isEmpty()) {
      return [bounds.min.x, bounds.min.y, bounds.min.z, bounds.max.x, bounds.max.y, bounds.max.z]
    }

    return [
      -DEFAULT_EXTENT,
      -DEFAULT_EXTENT,
      -DEFAULT_EXTENT,
      DEFAULT_EXTENT,
      DEFAULT_EXTENT,
      DEFAULT_EXTENT,
    ]
  }

  private getSpaceMousePivot(): THREE.Vector3 {
    return this.getEffectiveTargetPivot()
  }

  private getLookAt(): number[] | null {
    if (!this.services?.surfaceQuery || !this.domElement) {
      return null
    }

    const rect = this.domElement.getBoundingClientRect()
    const screenInWindow = this.currentMouseScreen
      .clone()
      .add(new THREE.Vector2(rect.left, rect.top))
    const minDistance = this.resolveSurfacePickMinDistance(screenInWindow, this.object)
    if (minDistance === null) {
      return null
    }
    const result = this.services.surfaceQuery.queryAtScreen(screenInWindow, this.object, {
      kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
      interactionType: 'move',
      interactionSource: 'spacemouse',
      windowSize: this.surfacePickWindowSize,
      ...(minDistance === undefined ? {} : { minDistance }),
    })

    if (this.selectionOnly && !result) {
      return null
    }

    return result?.point.toArray() ?? null
  }

  private getPointerPosition(): number[] {
    if (!this.domElement) {
      return [0, 0, 0]
    }

    const rect = this.domElement.getBoundingClientRect()
    const camera = this.deviceCamera
    const pos = new THREE.Vector3(
      (this.currentMouseScreen.x / Math.max(rect.width, 1)) * 2 - 1,
      -((this.currentMouseScreen.y / Math.max(rect.height, 1)) * 2 - 1),
      0
    )
    pos.unproject(camera)
    return pos.toArray()
  }

  private initializePivotRings(): void {
    this.ringGizmo.name = 'spaceMousePivotRings'
    this.ringGizmo.visible = false

    const xRing = this.createPivotRing(0xff6f61)
    xRing.rotation.y = Math.PI * 0.5

    const yRing = this.createPivotRing(0x6fe08f)
    yRing.rotation.x = Math.PI * 0.5

    const zRing = this.createPivotRing(0x6fa8ff)

    const centerSphereMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
    })
    this.centerSphereMaterial = centerSphereMaterial
    const centerSphere = new THREE.Mesh(
      new THREE.SphereGeometry(PIVOT_CENTER_BASE_RADIUS, 18, 18),
      centerSphereMaterial
    )
    centerSphere.name = 'spaceMousePivotCenter'

    this.ringGizmo.add(xRing, yRing, zRing, centerSphere)
    this.overlayScene.add(this.ringGizmo)
  }

  private createPivotRing(color: number): THREE.Mesh {
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.72,
      depthTest: false,
      depthWrite: false,
    })
    this.ringMaterials.push(material)

    return new THREE.Mesh(
      new THREE.TorusGeometry(PIVOT_RING_BASE_RADIUS, PIVOT_RING_TUBE_RADIUS, 20, 72),
      material
    )
  }

  private showPivotRings(): void {
    this.ringGizmo.visible = this.shouldShowPivotRings()
  }

  private hidePivotRings(): void {
    this.ringGizmo.visible = false
  }

  private updatePivotRings(camera: THREE.Camera): void {
    const displayPivot =
      this.pointerState === 'pan' && this.panGestureAnchor
        ? this.panGestureAnchor.point
        : this.pointerDragStarted || this.pointerState !== 'none'
          ? this.pivot
          : this.navIsActive
            ? this.getSpaceMousePivot()
            : this.pivot
    this.ringGizmo.position.copy(displayPivot)
    this.ringGizmo.visible = this.shouldShowPivotRings()

    const viewportHeight = Math.max(this.services?.getViewportSize().height ?? 0, 1)
    const distance = Math.max(camera.position.distanceTo(displayPivot), 1e-6)
    const radius = this.projectedRadius(PIVOT_RING_BASE_RADIUS, camera, distance, viewportHeight)
    const scale = radius <= 1e-6 ? 1 : PIVOT_RING_PIXEL_RADIUS / radius
    this.ringGizmo.scale.setScalar(scale)

    const centerSphere = this.ringGizmo.getObjectByName('spaceMousePivotCenter')
    if (centerSphere) {
      const centerRadius = this.projectedRadius(
        PIVOT_CENTER_BASE_RADIUS,
        camera,
        distance,
        viewportHeight
      )
      const centerScale = centerRadius <= 1e-6 ? 1 : PIVOT_CENTER_PIXEL_RADIUS / centerRadius
      centerSphere.scale.setScalar(centerScale / scale)
    }
  }

  private shouldShowPivotRings(): boolean {
    return (
      this.pivotGizmoEnabled &&
      this.enabled &&
      this.pendingPointerPivotPickVersion === 0 &&
      (this.pointerDragStarted || this.navIsActive)
    )
  }

  private projectedRadius(
    radius: number,
    camera: THREE.Camera,
    distance: number,
    viewportHeight: number
  ): number {
    if (camera instanceof THREE.PerspectiveCamera) {
      const fov = THREE.MathUtils.degToRad(camera.fov)
      return (viewportHeight * radius) / (2 * distance * Math.tan(fov / 2))
    }

    if (camera instanceof THREE.OrthographicCamera) {
      return (viewportHeight * radius * camera.zoom) / Math.max(camera.top - camera.bottom, 1e-6)
    }

    return 1
  }

  private cancelPendingPickRequests(): void {
    this.pickRequestVersion += 1
    this.pendingPointerPivotPickVersion = 0
    this.pickAbortController?.abort()
    this.pickAbortController = null
    this.wheelPivotPending = false
  }

  private disposePivotRings(): void {
    this.overlayScene.remove(this.ringGizmo)
    this.ringGizmo.traverse((child) => {
      const mesh = child as THREE.Mesh
      mesh.geometry?.dispose()
    })

    for (const material of this.ringMaterials) {
      material.dispose()
    }
    this.ringMaterials.length = 0
    this.centerSphereMaterial?.dispose()
    this.centerSphereMaterial = null
  }
}

export default SpaceMouseController
